import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ConfigProvider, Layout, Menu, Table, Button, Modal, Form, Input, Select, Segmented, DatePicker, message, Tag, Card, Row, Col, Statistic, Space, Switch, InputNumber, Upload, Progress } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { LaptopOutlined, NotificationOutlined, ToolOutlined, PlusOutlined, DashboardOutlined, HomeOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined, UploadOutlined, LockOutlined, PushpinOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, CloudUploadOutlined, DownloadOutlined, SearchOutlined, PhoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';

const { Header, Content, Sider } = Layout;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

dayjs.locale('zh-cn');

const BILL_TYPE_OPTIONS = [
  { label: '物业费', value: '物业费' },
  { label: '水电费', value: '水电费' },
  { label: '公用电费', value: '公用电费' },
  { label: '城市垃圾处理费', value: '城市垃圾处理费' },
  { label: '车库费', value: '车库费' }
];
const HOUSE_USAGE_OPTIONS = [
  { label: '住宅', value: '住宅' },
  { label: '商用', value: '商用' }
];
const HAS_GARAGE_FILTER_OPTIONS = [
  { label: '有车库', value: '1' },
  { label: '无车库', value: '0' }
];
const formatUsageType = (v) => v || '住宅';
const formatHasGarage = (v) => Number(v) === 1 ? '有' : '无';
const communityHasGarage = (c) => !!(c && (c.has_garage_facility === 1 || c.has_garage_facility === true || c.has_garage_facility === '1'));
const PARKING_BILL_TYPE_OPTIONS = [
  { label: '月租', value: '月租' },
  { label: '年租', value: '年租' },
  { label: '永久拥有', value: '永久拥有' },
  { label: '临停', value: '临停' }
];

// 房屋显示：楼栋、单元、房号 格式化为「X栋 X单元 X室」，避免只显示 1 1 1
const formatHouseLabel = (building, unit, room) => {
  const b = String(building ?? '').trim();
  const u = String(unit ?? '').trim();
  const r = String(room ?? '').trim();
  const bStr = b && !/栋|号楼/.test(b) ? b + '栋' : b;
  const uStr = u && !/单元/.test(u) ? u + '单元' : u;
  const rStr = r && !/室|号/.test(r) ? r + '室' : r;
  return [bStr, uStr, rStr].filter(Boolean).join(' ') || '—';
};

// 统一日期时间显示：ISO 转成 YYYY-MM-DD HH:mm
const formatDateTime = (t) => {
  if (!t) return '-';
  try {
    const d = new Date(t);
    if (isNaN(d.getTime())) return t;
    const Y = d.getFullYear();
    const M = String(d.getMonth() + 1).padStart(2, '0');
    const D = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${Y}-${M}-${D} ${h}:${m}`;
  } catch (_) { return t; }
};

// 计算报修处理时长：完成时间-创建时间；未完成则用当前时间
const formatDuration = (createTime, doneTime) => {
  if (!createTime) return '-';
  try {
    const start = new Date(createTime).getTime();
    const end = doneTime ? new Date(doneTime).getTime() : Date.now();
    if (isNaN(start) || isNaN(end) || end < start) return '-';
    const mins = Math.floor((end - start) / 60000);
    if (mins < 60) return `${mins} 分钟`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} 小时${mins % 60 ? ' ' + (mins % 60) + ' 分' : ''}`;
    const days = Math.floor(hours / 24);
    return `${days} 天${hours % 24 ? ' ' + (hours % 24) + ' 小时' : ''}`;
  } catch (_) { return '-'; }
};

const normalizeBillPeriod = (v) => String(v ?? '').trim().replace(/\s*至\s*/g, '至');
const isValidDateText = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(v + 'T00:00:00');
  if (isNaN(d.getTime())) return false;
  const [y, m, day] = v.split('-').map(n => parseInt(n, 10));
  return d.getFullYear() === y && d.getMonth() + 1 === m && d.getDate() === day;
};
const isValidBillPeriod = (v) => {
  const s = normalizeBillPeriod(v);
  if (/^\d{4}$/.test(s)) return true;
  if (/^\d{4}-\d{2}$/.test(s)) return true;
  if (/^\d{4}-\d{2}-\d{2}至\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [start, end] = s.split('至');
    return isValidDateText(start) && isValidDateText(end) && start <= end;
  }
  return false;
};
const PHONE_OR_LANDLINE_REGEX = /^(1\d{10}|0\d{2,3}-?\d{7,8})$/;
const billPeriodRule = {
  validator: (_, value) => {
    const s = normalizeBillPeriod(value);
    if (!s) return Promise.reject(new Error('必填'));
    if (isValidBillPeriod(s)) return Promise.resolve();
    return Promise.reject(new Error('填 2026、2026-03 或 2024-01-18至2025-02-28'));
  }
};
const formatBillPeriodByMode = (mode, values, prefix) => {
  if (mode === 'year') {
    const yearValue = values[`${prefix}_year`];
    return yearValue && yearValue.format ? yearValue.format('YYYY') : '';
  }
  if (mode === 'month') {
    const monthValue = values[`${prefix}_month`];
    return monthValue && monthValue.format ? monthValue.format('YYYY-MM') : '';
  }
  const rangeValue = values[`${prefix}_range`];
  if (!Array.isArray(rangeValue) || rangeValue.length !== 2) return '';
  const start = rangeValue[0] && rangeValue[0].format ? rangeValue[0].format('YYYY-MM-DD') : '';
  const end = rangeValue[1] && rangeValue[1].format ? rangeValue[1].format('YYYY-MM-DD') : '';
  return start && end ? `${start}至${end}` : '';
};
const formatParkingBillPeriod = (period, billType) => {
  if (billType === '永久拥有') return '永久';
  const text = String(period ?? '').trim();
  return text || '-';
};
const buildParkingBillPeriod = (startDate, endDate, billType) => {
  if (billType === '永久拥有') return null;
  if (!startDate || !endDate || !startDate.format || !endDate.format) return '';
  return `${startDate.format('YYYY-MM-DD')}至${endDate.format('YYYY-MM-DD')}`;
};
const getParkingBillDefaultDates = (billType) => {
  const start = dayjs().startOf('day');
  if (billType === '年租') return { start, end: start.add(12, 'month') };
  if (billType === '月租') return { start, end: start.add(1, 'month') };
  if (billType === '永久拥有') return { start: null, end: null };
  return { start, end: start.add(1, 'day') };
};

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.substring(1);
  const activeTab = path || 'dashboard';

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [stats, setStats] = useState({ unpaidBills: 0, pendingRepairs: 0, totalNotices: 0, unpaidAmount: 0, boundHouses: 0, unboundHouses: 0, totalHouses: 0, approvedBindCount: 0, paidHouseCount: 0, paidTotalAmount: 0, garagePaidAmount: 0, feedbackToday: 0, feedbackTotal: 0, repairToday: 0, auditPending: 0, hasNewFeedback: false, hasNewRepair: false, hasNewAudit: false });
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingItem, setEditingItem] = useState(null);
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginForm] = Form.useForm();
  const [initLoading, setInitLoading] = useState(true);
  
  const [fileList, setFileList] = useState([]);

  const [communityList, setCommunityList] = useState([]);
  const [houseList, setHouseList] = useState([]);
  const [billEntryMode, setBillEntryMode] = useState('single');
  const [addHouseBuilding, setAddHouseBuilding] = useState('');
  const [addHouseUnit, setAddHouseUnit] = useState('');
  const [addHouseRoom, setAddHouseRoom] = useState('');
  const [addHouseLoading, setAddHouseLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateForm] = Form.useForm();
  const [generateType, setGenerateType] = useState('year'); // year=按年(物业费) month=按月(电费等)
  const [generateMode, setGenerateMode] = useState('fixed'); // fixed=固定金额 by_area=按面积×单价（仅按年时有效）
  const [billPeriodMode, setBillPeriodMode] = useState('range');
  const [addHouseArea, setAddHouseArea] = useState('');
  const [addHouseBasementArea, setAddHouseBasementArea] = useState('');
  const [addHouseUsageType, setAddHouseUsageType] = useState('住宅');
  const [addHouseHasGarage, setAddHouseHasGarage] = useState(false);
  const [addHouseGarageArea, setAddHouseGarageArea] = useState('');
  const [isAreaModalVisible, setIsAreaModalVisible] = useState(false);
  const [noAreaHouseList, setNoAreaHouseList] = useState([]);
  const [areaInputValues, setAreaInputValues] = useState({});
  const [basementInputValues, setBasementInputValues] = useState({});
  const [areaSaveLoading, setAreaSaveLoading] = useState(false);
  const [isBindAddModalVisible, setIsBindAddModalVisible] = useState(false);
  const [bindAddForm] = Form.useForm();
  const [isAuditEditVisible, setIsAuditEditVisible] = useState(false);
  const [auditEditForm] = Form.useForm();
  const [auditEditSubmitting, setAuditEditSubmitting] = useState(false);
  const [editingAuditId, setEditingAuditId] = useState(null);
  
  const [isPwdModalVisible, setIsPwdModalVisible] = useState(false);
  const [pwdForm] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignRepairId, setAssignRepairId] = useState(null);
  const [assignForm] = Form.useForm();
  const [urgeLoading, setUrgeLoading] = useState(false);
  const [houseSearchResults, setHouseSearchResults] = useState([]);
  const [houseSearchLoading, setHouseSearchLoading] = useState(false);
  const [lastSearchParams, setLastSearchParams] = useState({});
  const [houseEditModalVisible, setHouseEditModalVisible] = useState(false);
  const [houseEditForm] = Form.useForm();
  const [houseSearchForm] = Form.useForm();
  const [houseAddModalVisible, setHouseAddModalVisible] = useState(false);
  const [houseAddForm] = Form.useForm();
  const [houseAddLoading, setHouseAddLoading] = useState(false);
  const [butlerForm] = Form.useForm();
  const [butlerSaveLoading, setButlerSaveLoading] = useState(false);
  const [isParkingSpaceModalVisible, setIsParkingSpaceModalVisible] = useState(false);
  const [parkingSpaceForm] = Form.useForm();
  const [isParkingBillModalVisible, setIsParkingBillModalVisible] = useState(false);
  const [parkingBillForm] = Form.useForm();
  const generateFormCommunityId = Form.useWatch('community_id', generateForm);
  const generateBillType = Form.useWatch('bill_type', generateForm);
  const selectedGenerateCommunity = communityList.find(c => c.id === generateFormCommunityId);
  const generateGarageBillDisabled = generateBillType === '车库费' && generateFormCommunityId && !communityHasGarage(selectedGenerateCommunity);
  const billEntryCommunityId = Form.useWatch('community_id', form);
  const selectedBillEntryCommunity = communityList.find(c => c.id === billEntryCommunityId);
  const parkingBillType = Form.useWatch('bill_type', parkingBillForm);
  const [parkingBillSubmitLoading, setParkingBillSubmitLoading] = useState(false);
  const [parkingBillSpaceList, setParkingBillSpaceList] = useState([]);
  const [pointsPrizeData, setPointsPrizeData] = useState([]);
  const [pointsOrderData, setPointsOrderData] = useState([]);
  const [pointsStats, setPointsStats] = useState(null);
  const [pointsConfig, setPointsConfig] = useState({});
  const [isPointsPrizeModalVisible, setIsPointsPrizeModalVisible] = useState(false);
  const [pointsPrizeForm] = Form.useForm();
  const [pointsPrizeSubmitting, setPointsPrizeSubmitting] = useState(false);
  const [pointsPrizeFileList, setPointsPrizeFileList] = useState([]);
  const [editingPointsPrizeId, setEditingPointsPrizeId] = useState(null);
  const [isPointsOrderAuditVisible, setIsPointsOrderAuditVisible] = useState(false);
  const [auditingOrderId, setAuditingOrderId] = useState(null);
  const [pointsAuditRejectReason, setPointsAuditRejectReason] = useState('');
  const [pointsAdjustForm] = Form.useForm();
  const [pointsConfigForm] = Form.useForm();
  const [pointsAdjustSubmitting, setPointsAdjustSubmitting] = useState(false);
  const [pointsConfigSubmitting, setPointsConfigSubmitting] = useState(false);
  const [pointsAccountData, setPointsAccountData] = useState([]);
  const [pointsAccountLoading, setPointsAccountLoading] = useState(false);
  const [pointsAccountKeyword, setPointsAccountKeyword] = useState('');

  const [paidHistoryData, setPaidHistoryData] = useState([]);
  const [paidHistorySummary, setPaidHistorySummary] = useState({ total: 0, totalAmount: 0, todayCount: 0, todayAmount: 0, garageCount: 0, garageAmount: 0 });
  const [paidHistoryLoading, setPaidHistoryLoading] = useState(false);
  const [paidHistoryFilter, setPaidHistoryFilter] = useState({ range: null, quickDay: '', billType: '', keyword: '', includeParking: true, page: 1, pageSize: 20 });

  const getAuthHeaders = () => {
    try {
      const u = JSON.parse(localStorage.getItem('admin_user') || '{}');
      return u.token ? { Authorization: `Bearer ${u.token}` } : {};
    } catch (_) { return {}; }
  };
  const applyParkingBillTypeDefaults = (billType, force = false) => {
    const type = billType || parkingBillForm.getFieldValue('bill_type') || '月租';
    if (type === '永久拥有') {
      parkingBillForm.setFieldsValue({ parking_start_date: null, parking_end_date: null });
      return;
    }
    const currentStart = parkingBillForm.getFieldValue('parking_start_date');
    const currentEnd = parkingBillForm.getFieldValue('parking_end_date');
    if (!force && currentStart && currentEnd) return;
    const defaults = getParkingBillDefaultDates(type);
    parkingBillForm.setFieldsValue({
      parking_start_date: defaults.start,
      parking_end_date: defaults.end
    });
  };
  const openParkingBillModal = () => {
    parkingBillForm.resetFields();
    setParkingBillSpaceList([]);
    setIsParkingBillModalVisible(true);
    parkingBillForm.setFieldsValue({ bill_type: '月租' });
    applyParkingBillTypeDefaults('月租', true);
  };

  useEffect(() => {
    const userStr = localStorage.getItem('admin_user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (!user.token) {
          localStorage.removeItem('admin_user');
        } else {
          setIsLoggedIn(true);
          setCurrentUser(user);
        }
      } catch (e) { localStorage.removeItem('admin_user'); }
    }
    setInitLoading(false);
  }, []);

  useEffect(() => {
    if (!initLoading && isLoggedIn && !path) {
      navigate('/dashboard');
    }
  }, [initLoading, isLoggedIn, path, navigate]);

  useEffect(() => { 
    if (!isLoggedIn) return;
    setSelectedRowKeys([]);
    if (activeTab === 'dashboard') fetchStats();
    else fetchData(); 
  }, [activeTab, isLoggedIn]);

  // 账单/业主审核/车位：拉取小区列表
  useEffect(() => {
    if (activeTab === 'bills' || activeTab === 'audit' || activeTab === 'houses' || activeTab === 'parking' || activeTab === 'parkingBills') {
      fetch('/api/community/list', { headers: getAuthHeaders() }).then(r => r.json()).then(list => setCommunityList(Array.isArray(list) ? list : []));
    }
    if (isModalVisible && activeTab === 'bills' && !editingItem) {
      setHouseList([]);
      form.setFieldsValue({ community_id: undefined, house_id: undefined, house_ids: undefined });
    }
  }, [isModalVisible, activeTab, editingItem]);

  useEffect(() => {
    if (generateBillType === '车库费' && generateType === 'year') setGenerateMode('by_area');
  }, [generateBillType, generateType]);

  useEffect(() => {
    if (!searchText) {
      setFilteredData(data);
    } else {
      const lower = searchText.toLowerCase();
      const filtered = data.filter(item => {
        if (activeTab === 'menus') return item.name.includes(lower);
        if (activeTab === 'communities') return item.name.includes(lower);
        if (activeTab === 'notices') return item.title.includes(lower);
        if (activeTab === 'audit') return item.name.includes(lower) || item.phone.includes(lower);
        if (activeTab === 'feedback') return (item.phone && item.phone.includes(lower)) || (item.content && item.content.includes(lower));
        if (activeTab === 'parking') return (item.code && item.code.includes(lower)) || (item.phone && item.phone.includes(lower));
        if (activeTab === 'parkingBills') return (item.space_code && item.space_code.includes(lower)) || (item.month && item.month.includes(lower));
        if (activeTab === 'pointsPrize') return (item.name && item.name.includes(lower)) || (item.category && item.category.includes(lower));
        if (activeTab === 'pointsOrders') return (item.phone && item.phone.includes(lower)) || (item.prize_name && item.prize_name.includes(lower));
        return String(item.id).includes(lower);
      });
      setFilteredData(filtered);
    }
  }, [data, searchText, activeTab]);

  const handleLogin = async (values) => {
    try {
      const apiBase = window.location.origin;
      const res = await fetch(`${apiBase}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: values.username, password: values.password }),
      });
      const data = await res.json();
      
      if (res.status === 200 && data.token) {
        localStorage.setItem('admin_user', JSON.stringify(data));
        setIsLoggedIn(true);
        setCurrentUser(data);
        message.success(`欢迎回来，${data.nickname}`);
        navigate('/dashboard');
      } else if (res.status === 200 && !data.token) {
        message.error('登录接口需更新，请部署最新 server.js 后重试', 6);
      } else {
        message.error(data.error || '账号或密码错误', 6);
      }
    } catch (err) {
      message.error('登录请求失败，请检查网络或服务器');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_user');
    setIsLoggedIn(false);
    setCurrentUser(null);
    message.success('已退出登录');
    navigate('/');
  };

  const handleChangePassword = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的新密码不一致');
      return;
    }
    try {
      const res = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          username: currentUser?.username,
          oldPassword: values.oldPassword,
          newPassword: values.newPassword
        })
      });
      const data = await res.json();
      if (res.status === 401) {
        setIsPwdModalVisible(false);
        pwdForm.resetFields();
        localStorage.removeItem('admin_user');
        setIsLoggedIn(false);
        setCurrentUser(null);
        message.error(data.error || '登录已过期，请重新登录');
        navigate('/', { replace: true });
        return;
      }
      if (res.ok && data.success) {
        setIsPwdModalVisible(false);
        pwdForm.resetFields();
        localStorage.removeItem('admin_user');
        setIsLoggedIn(false);
        setCurrentUser(null);
        message.success('密码已修改，请使用新密码重新登录');
        navigate('/', { replace: true });
      } else {
        message.error(data.error);
      }
    } catch (e) { message.error('请求失败'); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/stats', { headers: getAuthHeaders() });
      if (res.status === 401) {
        localStorage.removeItem('admin_user');
        setIsLoggedIn(false);
        setCurrentUser(null);
        message.error('登录已过期，请重新登录');
        navigate('/');
        return;
      }
      const json = await res.json();
      setStats(json);
      fetchPaidHistory();
      fetchTrend();
    } catch (err) { message.error('获取统计失败'); }
  };

  const fetchTrend = async () => {
    try {
      const res = await fetch('/api/stats/trend?days=30', { headers: getAuthHeaders() });
      if (!res.ok) return;
      const json = await res.json();
      setTrend(json);
    } catch (err) { /* 趋势为增强项，失败不打扰用户 */ }
  };

  const resolveQuickDayRange = (key) => {
    if (!key) return null;
    let offset = 0;
    if (key === 'today') offset = 0;
    else if (key === 'yesterday') offset = 1;
    else if (key === 'day-before-yesterday') offset = 2;
    else return null;
    const d = dayjs().subtract(offset, 'day').format('YYYY-MM-DD');
    return { from: d, to: d };
  };

  const fetchPaidHistory = async (nextFilter) => {
    const f = { ...paidHistoryFilter, ...(nextFilter || {}) };
    setPaidHistoryFilter(f);
    setPaidHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', f.page);
      params.set('pageSize', f.pageSize);
      if (f.includeParking) params.set('includeParking', '1');
      const quickRange = resolveQuickDayRange(f.quickDay);
      if (quickRange) {
        params.set('dateFrom', quickRange.from);
        params.set('dateTo', quickRange.to);
      } else {
        if (f.range && f.range[0]) params.set('dateFrom', f.range[0].format('YYYY-MM-DD'));
        if (f.range && f.range[1]) params.set('dateTo', f.range[1].format('YYYY-MM-DD'));
      }
      if (f.billType) params.set('billType', f.billType);
      if (f.keyword) params.set('keyword', f.keyword);
      const res = await fetch('/api/bill/paid-history?' + params.toString(), { headers: getAuthHeaders() });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setPaidHistoryData(json.list || []);
        setPaidHistorySummary({ total: json.total || 0, totalAmount: json.totalAmount || 0, todayCount: json.todayCount || 0, todayAmount: json.todayAmount || 0, garageCount: json.garageCount || 0, garageAmount: json.garageAmount || 0 });
      } else {
        message.error(json.error || '缴费历史加载失败');
      }
    } catch (e) {
      message.error('缴费历史加载失败：' + (e.message || ''));
    } finally {
      setPaidHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'butler') {
      fetch('/api/config/butler').then(r => r.json()).then(d => {
        butlerForm.setFieldsValue({
          butler_phone: d.phone || '',
          butler_name: d.name || '',
          butler_work_am: d.workTimeAm || '08:00-12:00',
          butler_work_pm: d.workTimePm || '14:00-18:00'
        });
      }).catch(() => {});
    }
  }, [activeTab]);

  const saveButler = async () => {
    try {
      const v = await butlerForm.validateFields();
      setButlerSaveLoading(true);
      const res = await fetch('/api/config/butler', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ phone: v.butler_phone, name: v.butler_name, workTimeAm: v.butler_work_am, workTimePm: v.butler_work_pm }) });
      const data = await res.json();
      if (res.ok) message.success('已保存');
      else message.error(data.error || '保存失败');
    } catch (e) { if (!e.errorFields) message.error('保存失败'); }
    finally { setButlerSaveLoading(false); }
  };

  const fetchData = async () => {
    if (activeTab === 'houses') return;
    setLoading(true);
    let url = '';
    if (activeTab === 'bills') url = '/api/bill/list';
    if (activeTab === 'repairs') url = '/api/repair/list';
    if (activeTab === 'notices') url = '/api/notice/list';
    if (activeTab === 'communities') url = '/api/community/list';
    if (activeTab === 'menus') url = '/api/menu/all';
    if (activeTab === 'audit') url = '/api/bind/list';
    if (activeTab === 'feedback') url = '/api/feedback/list';
    if (activeTab === 'parking') url = '/api/parking/space/list';
    if (activeTab === 'parkingBills') url = '/api/parking/bill/list';
    if (activeTab === 'pointsPrize') url = '/api/points/admin/prize/list';
    if (activeTab === 'pointsOrders') url = '/api/points/admin/order/list';

    if (url) {
      try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(url + sep + '_=' + Date.now(), { cache: 'no-store', headers: getAuthHeaders() });
        if (res.status === 401) {
          localStorage.removeItem('admin_user');
          setIsLoggedIn(false);
          setCurrentUser(null);
          message.error('登录已过期，请重新登录');
          navigate('/');
          setLoading(false);
          return;
        }
        const json = await res.json();
        const list = Array.isArray(json) ? json : [];
        const processed = list.map(item => ({ ...item, key: item.id }));
        setData(processed);
        setFilteredData(processed);
        if (activeTab === 'pointsPrize') setPointsPrizeData(processed);
        if (activeTab === 'pointsOrders') setPointsOrderData(processed);
      } catch (err) { message.error('加载失败'); }
    }
    if (activeTab === 'pointsManage') {
      fetch('/api/points/admin/stats', { headers: getAuthHeaders() }).then(r => r.json()).then(s => setPointsStats(s)).catch(() => setPointsStats(null));
      setPointsAccountLoading(true);
      fetch('/api/points/admin/account/list', { headers: getAuthHeaders() })
        .then(r => r.json())
        .then(list => { setPointsAccountData(Array.isArray(list) ? list : []); })
        .catch(() => setPointsAccountData([]))
        .finally(() => setPointsAccountLoading(false));
    }
    if (activeTab === 'pointsConfig') {
      fetch('/api/points/admin/config', { headers: getAuthHeaders() }).then(r => r.json()).then(c => {
        const v = { ...c, clear_enabled: c.clear_enabled === 1 || c.clear_enabled === true };
        setPointsConfig(v);
        pointsConfigForm.setFieldsValue(v);
      }).catch(() => {});
    }
    setLoading(false);
  };

  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch('/api/bill/import', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        message.success(data.message);
        fetchData();
      } else {
        message.error(data.error);
      }
    } catch (err) {
      message.error('导入失败');
    }
    return false; // 阻止自动上传
  };

  const handleGenerate = async () => {
    try {
      const v = await generateForm.validateFields();
      const period = normalizeBillPeriod(generateType === 'year' ? formatBillPeriodByMode('range', v, 'generate_period') : formatBillPeriodByMode('month', v, 'generate_period'));
      if (!isValidBillPeriod(period)) {
        message.error('请选择正确收费周期');
        return;
      }
      setGenerateLoading(true);
      const isGarageGen = v.bill_type === '车库费';
      const useByArea = generateType === 'year' && (generateMode === 'by_area' || isGarageGen);
      const res = await fetch('/api/bill/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          community_id: v.community_id || null,
          month: period,
          amount: parseFloat(v.amount),
          unit_price_basement: (generateType === 'year' && useByArea && !isGarageGen && v.unit_price_basement != null && v.unit_price_basement !== '') ? parseFloat(v.unit_price_basement) : undefined,
          mode: useByArea ? 'by_area' : undefined,
          bill_type: BILL_TYPE_OPTIONS.some(o => o.value === v.bill_type) ? v.bill_type : '物业费'
        })
      });
      const data = await res.json();
      if (res.ok) {
        message.success(data.message || '生成成功');
        generateForm.resetFields();
        fetchData();
      } else {
        message.error(data.error || '生成失败');
      }
    } catch (e) {
      if (e.errorFields) message.error('请填写完整');
    } finally {
      setGenerateLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch('/api/bill/template', { headers: getAuthHeaders() });
      if (!res.ok) { message.error(res.status === 401 ? '请先登录' : '下载失败'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bill-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success('模板已下载');
    } catch (_) { message.error('下载失败'); }
  };

  const openAreaModal = async () => {
    const community_id = generateForm.getFieldValue('community_id');
    if (!community_id) { message.warning('请先选择小区'); return; }
    try {
      const res = await fetch('/api/house/list-no-area?community_id=' + community_id, { headers: getAuthHeaders() });
      const list = await res.json();
      if (!Array.isArray(list)) throw new Error('请求失败');
      setNoAreaHouseList(list);
      setAreaInputValues(list.reduce((acc, h) => ({ ...acc, [h.id]: h.area != null ? String(h.area) : '' }), {}));
      setBasementInputValues(list.reduce((acc, h) => ({ ...acc, [h.id]: h.basement_area != null ? String(h.basement_area) : '' }), {}));
      if (list.length === 0) {
        message.success('该小区所有房屋已填写面积');
        return;
      }
      setIsAreaModalVisible(true);
    } catch (e) {
      message.error('获取未填面积房屋失败');
    }
  };

  const saveHouseArea = async (houseId, areaStr, basementStr) => {
    const area = areaStr !== '' && areaStr != null ? parseFloat(areaStr) : null;
    const basement = basementStr !== '' && basementStr != null ? parseFloat(basementStr) : null;
    const areaOk = area != null && !isNaN(area) && area > 0;
    const baseOk = basement != null && !isNaN(basement) && basement >= 0;
    if (!areaOk && !baseOk) { message.warning('请至少填写房屋面积(>0)或地下室面积(≥0)'); return; }
    setAreaSaveLoading(true);
    try {
      const res = await fetch('/api/house/update-area', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ id: houseId, area: areaOk ? area : undefined, basement_area: baseOk ? basement : undefined })
      });
      const data = await res.json();
      if (res.ok) {
        message.success('面积已保存');
        if (areaOk && baseOk) setNoAreaHouseList(prev => prev.filter(h => h.id !== houseId));
      } else {
        message.error(data.error || '保存失败');
      }
    } catch (e) { message.error('请求失败'); }
    setAreaSaveLoading(false);
  };

  const addHouseInBillModal = async () => {
    const community_id = form.getFieldValue('community_id');
    if (!community_id) { message.warning('请先选择小区'); return; }
    const building = (addHouseBuilding || '').trim();
    const unit = (addHouseUnit || '').trim();
    const room = (addHouseRoom || '').trim();
    if (!building || !unit || !room) { message.warning('请填写楼栋、单元、房号'); return; }
    setAddHouseLoading(true);
    try {
      const res = await fetch('/api/house/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ community_id, building, unit, room, area: addHouseArea ? parseFloat(addHouseArea) : undefined, basement_area: addHouseBasementArea !== '' ? parseFloat(addHouseBasementArea) : undefined, usage_type: addHouseUsageType || '住宅', has_garage: addHouseHasGarage ? 1 : 0, garage_area: addHouseHasGarage && addHouseGarageArea !== '' ? parseFloat(addHouseGarageArea) : 0 })
      });
      const data = await res.json();
      if (res.ok && data.id) {
        message.success(data.message || '添加成功');
        setAddHouseBuilding(''); setAddHouseUnit(''); setAddHouseRoom(''); setAddHouseArea(''); setAddHouseBasementArea('');
        setAddHouseUsageType('住宅'); setAddHouseHasGarage(false); setAddHouseGarageArea('');
        const listRes = await fetch('/api/house/list?community_id=' + community_id, { headers: getAuthHeaders() });
        const list = await listRes.json();
        setHouseList(Array.isArray(list) ? list : []);
        if (billEntryMode === 'single') form.setFieldsValue({ house_id: data.id });
        else form.setFieldsValue({ house_ids: [...(form.getFieldValue('house_ids') || []), data.id] });
      } else {
        if (res.status === 404) message.error('接口未找到：请把最新 server.js 上传到服务器并重启 Node 项目');
        else message.error(data.error || '添加失败');
      }
    } catch (e) { message.error('请求失败，请检查网络或服务器是否已更新 server.js 并重启'); }
    setAddHouseLoading(false);
  };

  const handleSubmit = async (values) => {
    let url = '';
    if (activeTab === 'bills') url = billEntryMode === 'batch' ? '/api/bill/batch' : '/api/bill/add';
    if (activeTab === 'notices') url = editingItem ? '/api/notice/update' : '/api/notice/add';
    if (activeTab === 'communities') url = editingItem ? '/api/community/update' : '/api/community/add';
    if (activeTab === 'menus') url = editingItem ? '/api/menu/update' : '/api/menu/add';

    try {
      let body = { ...values };
      if (activeTab === 'bills') {
        const billType = BILL_TYPE_OPTIONS.some(o => o.value === values.bill_type) ? values.bill_type : '物业费';
        const period = normalizeBillPeriod(formatBillPeriodByMode(billPeriodMode, values, 'bill_period'));
        if (!isValidBillPeriod(period)) { message.error('请选择正确周期'); return; }
        if (billEntryMode === 'batch') {
          body = { community_id: values.community_id, month: period, amount: values.amount, house_ids: values.house_ids || [], bill_type: billType };
          if (!body.house_ids.length) { message.error('请至少选择一户'); return; }
        } else {
          body = { community_id: values.community_id, house_id: values.house_id, amount: values.amount, month: period, mark_paid: !!values.mark_paid, bill_type: billType };
        }
      }
      if (activeTab === 'menus') body.is_visible = values.is_visible ? 1 : 0;
      if (activeTab === 'communities') {
        body.has_garage_facility = values.has_garage_facility ? 1 : 0;
        body.garage_unit_price = values.garage_unit_price != null && values.garage_unit_price !== '' ? values.garage_unit_price : 0;
      }
      
      if (activeTab === 'notices') {
        body.is_top = values.is_top ? 1 : 0; // 处理置顶字段
        const imageUrls = fileList.map(file => {
          if (file.response) return file.response.url;
          return file.url;
        }).filter(url => url);
        body.image_url = imageUrls.length > 0 ? imageUrls[0] : null;
      }
      
      if (editingItem) body.id = editingItem.id;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) { message.error(data.error || '提交失败'); return; }
      message.success(data.message || '操作成功');
      setIsModalVisible(false);
      setEditingItem(null);
      setFileList([]);
      form.resetFields();
      fetchData();
    } catch (err) { message.error('提交失败'); }
  };

  const deleteItem = async (id, endpoint) => {
    const names = { bill: '账单', repair: '报修', notice: '公告', community: '小区', menu: '菜单' };
    Modal.confirm({
      title: '确认删除',
      content: `删除该${names[endpoint] || '项'}后无法恢复，确定要删除吗？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await fetch(`/api/${endpoint}/delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ id })
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok) { message.success('删除成功'); fetchData(); }
          else message.error(data.error || '删除失败');
        } catch (e) { message.error('删除失败'); }
      }
    });
  };

  const deleteHouse = (row) => {
    const addrText = `${row.community_name || ''} ${row.building || ''} ${row.unit || ''} ${row.room || ''}`.trim() || `ID ${row.id}`;
    const doDelete = async (force) => {
      try {
        const res = await fetch('/api/house/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ id: row.id, force: !!force })
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          const d = data.deleted || {};
          const extra = [];
          if (d.bill) extra.push(`账单 ${d.bill} 条`);
          if (d.repair) extra.push(`报修 ${d.repair} 条`);
          if (d.bind) extra.push(`绑定 ${d.bind} 条`);
          message.success(extra.length ? `删除成功，同时清理：${extra.join('、')}` : '删除成功');
          runHouseSearch(lastSearchParams);
          return true;
        }
        if (res.status === 409 && data && data.error === 'has_related_data') {
          const parts = [];
          if (data.bill_count) parts.push(`账单 ${data.bill_count} 条`);
          if (data.repair_count) parts.push(`报修 ${data.repair_count} 条`);
          if (data.bind_count) parts.push(`业主绑定 ${data.bind_count} 条`);
          Modal.confirm({
            title: '该房屋还有关联数据',
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>{addrText} 下还有：</div>
                <div style={{ color: '#d4380d', marginBottom: 8 }}>{parts.join('、')}</div>
                <div>是否一并删除？此操作不可恢复，请确认是输错楼栋等错误数据再操作。</div>
              </div>
            ),
            okText: '一并删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: () => doDelete(true)
          });
          return false;
        }
        message.error(data.error || '删除失败');
      } catch (e) { message.error('删除失败'); }
    };
    Modal.confirm({
      title: '确认删除房屋',
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>将删除房屋：</div>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{addrText}</div>
          <div style={{ color: '#999' }}>删除后无法恢复。若该房屋已有账单、报修或业主绑定，将再次提示是否一并清理。</div>
        </div>
      ),
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => doDelete(false)
    });
  };

  // 批量删除：仅超级管理员、且当前 tab 支持删除时可用
  const tabToEndpoint = { bills: 'bill', repairs: 'repair', notices: 'notice', communities: 'community', menus: 'menu' };
  const batchDeleteSupported = currentUser?.role === 'SUPER_ADMIN' && tabToEndpoint[activeTab];
  const batchDeleteSelected = () => {
    if (!selectedRowKeys.length) return;
    const endpoint = tabToEndpoint[activeTab];
    const names = { bill: '账单', repair: '报修', notice: '公告', community: '小区', menu: '菜单' };
    const name = names[endpoint] || '项';
    Modal.confirm({
      title: '确认批量删除',
      content: `将删除选中的 ${selectedRowKeys.length} 条${name}，删除后无法恢复，确定吗？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const hide = message.loading(`正在删除 ${selectedRowKeys.length} 条…`, 0);
        try {
          await Promise.all(selectedRowKeys.map(id =>
            fetch(`/api/${endpoint}/delete`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
              body: JSON.stringify({ id })
            })
          ));
          hide();
          message.success('批量删除成功');
          setSelectedRowKeys([]);
          fetchData();
        } catch (e) {
          hide();
          message.error('批量删除失败');
        }
      }
    });
  };

  const openEdit = (record) => {
    setEditingItem(record);
    const values = { ...record };
    if (activeTab === 'menus') values.is_visible = record.is_visible === 1;
    if (activeTab === 'notices') values.is_top = record.is_top === 1;
    if (activeTab === 'communities') values.has_garage_facility = record.has_garage_facility === 1 || record.has_garage_facility === true;
    
    if (activeTab === 'notices' && record.image_url) {
      try {
        setFileList([{ uid: -1, name: 'image.png', status: 'done', url: record.image_url }]);
      } catch (e) { setFileList([]); }
    } else {
      setFileList([]);
    }

    form.setFieldsValue(values);
    setIsModalVisible(true);
  };

  const openAdd = () => {
    setEditingItem(null);
    setFileList([]);
    form.resetFields();
    if (activeTab === 'menus') form.setFieldsValue({ is_visible: true, sort_order: 0 });
    if (activeTab === 'notices') form.setFieldsValue({ is_top: false }); // 默认不置顶
    if (activeTab === 'communities') form.setFieldsValue({ has_garage_facility: false, garage_unit_price: 0 });
    if (activeTab === 'bills') setBillPeriodMode('range');
    setIsModalVisible(true);
  };

  const updateRepairStatus = async (id, status) => {
    try {
      const res = await fetch('/api/repair/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ id, status })
      });
      const data = await res.json();
      if (!res.ok) { message.error(data.error || '更新失败'); return; }
      message.success('状态已更新');
      fetchData();
    } catch (e) { message.error('更新失败'); }
  };

  const openAssignModal = (record) => {
    setAssignRepairId(record.id);
    assignForm.setFieldsValue({ handler: record.handler || '' });
    setAssignModalVisible(true);
  };

  const submitAssign = async () => {
    try {
      const values = await assignForm.validateFields();
      const res = await fetch('/api/repair/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ id: assignRepairId, handler: values.handler })
      });
      const data = await res.json();
      if (!res.ok) { message.error(data.error || '派单失败'); return; }
      message.success('派单成功');
      setAssignModalVisible(false);
      setAssignRepairId(null);
      fetchData();
    } catch (e) {
      if (e && e.errorFields) return;
      message.error('派单失败');
    }
  };

  const batchUrgeSelected = async () => {
    if (!selectedRowKeys.length) return;
    const unpaidIds = data
      .filter(d => selectedRowKeys.includes(d.id) && d.status !== 'PAID')
      .map(d => d.id);
    if (unpaidIds.length === 0) {
      message.warning('所选账单均已缴费，无需催缴');
      return;
    }
    Modal.confirm({
      title: '批量催缴',
      content: `将对选中的 ${unpaidIds.length} 笔未缴账单发起催缴，业主将在小程序账单页收到提醒。确定吗？`,
      okText: '确认催缴',
      cancelText: '取消',
      onOk: async () => {
        setUrgeLoading(true);
        try {
          const res = await fetch('/api/bill/urge-batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ billIds: unpaidIds })
          });
          const d = await res.json();
          if (!res.ok) { message.error(d.error || '催缴失败'); return; }
          const parts = [`成功催缴 ${d.sent || 0} 户`];
          if (d.skippedNoPhone) parts.push(`${d.skippedNoPhone} 户未绑定业主已跳过`);
          if (d.skippedPaid) parts.push(`${d.skippedPaid} 笔已缴已跳过`);
          message.success(parts.join('，'));
          setSelectedRowKeys([]);
        } catch (e) {
          message.error('催缴失败');
        } finally {
          setUrgeLoading(false);
        }
      }
    });
  };

  // 快速切换置顶状态
  const toggleNoticeTop = async (id, currentTop) => {
    try {
      const res = await fetch('/api/notice/toggle-top', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ id, is_top: !currentTop })
      });
      const data = await res.json();
      if (!res.ok) { message.error(data.error || '更新失败'); return; }
      message.success('置顶状态已更新');
      fetchData();
    } catch (e) { message.error('更新失败'); }
  };
  
  const handleAuditEdit = (record) => {
    setEditingAuditId(record.id);
    auditEditForm.setFieldsValue({
      name: record.name,
      phone: record.phone,
      area: record.area,
      basement_area: record.basement_area,
      usage_type: record.usage_type || '住宅',
      has_garage: Number(record.has_garage) === 1,
      garage_area: record.garage_area || 0
    });
    setIsAuditEditVisible(true);
  };
  const handleAuditEditSubmit = async (values) => {
    const id = editingAuditId != null ? editingAuditId : values.id;
    if (id == null || id === '') {
      message.error('缺少申请ID');
      return;
    }
    if (auditEditSubmitting) return;
    setAuditEditSubmitting(true);
    try {
      const res = await fetch('/api/bind/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          id,
          name: values.name,
          phone: values.phone,
          area: values.area,
          basement_area: values.basement_area,
          usage_type: values.usage_type || '住宅',
          has_garage: values.has_garage ? 1 : 0,
          garage_area: values.has_garage ? (values.garage_area || 0) : 0
        })
      });
      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch (_) {}
      if (res.ok && data.success) {
        message.success('已保存');
        setIsAuditEditVisible(false);
        setEditingAuditId(null);
        fetchData();
      } else message.error(data.error || (res.status === 404 ? '接口未就绪，请确认后端已部署并重启' : '保存失败'));
    } catch (_) { message.error('请求失败，请检查网络或后端是否运行'); }
    finally { setAuditEditSubmitting(false); }
  };
  const handleAudit = async (id, status, remark) => {
    try {
      const res = await fetch('/api/bind/audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ id, status, remark: remark || '' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data && data.error)) {
        message.error((data && data.error) || '操作失败');
        return false;
      }
      message.success(status === 'APPROVED' ? '已通过' : '已驳回');
      fetchData();
      fetchStats();
      return true;
    } catch (e) { message.error('请求失败'); return false; }
  };

  // 删除业主审核申请：超级管理员可操作；不动 house/bill，仅删申请记录
  const deleteAuditApply = (row) => {
    const addr = `${row.community || ''} ${row.building || ''} ${row.unit || ''} ${row.house || ''}`.trim();
    const statusText = row.status === 'APPROVED' ? '已通过' : row.status === 'REJECTED' ? '已驳回' : '审核中';
    Modal.confirm({
      title: '删除业主审核申请',
      width: 480,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>申请人：<b>{row.name || '-'}</b>（{row.phone || '-'}）</div>
          <div style={{ marginBottom: 8 }}>申请房屋：{addr || '-'}</div>
          <div style={{ marginBottom: 8 }}>状态：{statusText}</div>
          <div style={{ color: '#d4380d' }}>仅删除这条申请记录，不影响房屋、账单等关联数据。删除后无法恢复。</div>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await fetch('/api/bind/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ id: row.id })
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok) {
            message.success('删除成功');
            fetchData();
            fetchStats();
          } else {
            message.error(data.error || '删除失败');
          }
        } catch (e) { message.error('请求失败'); }
      }
    });
  };

  // 通过/驳回弹窗：允许填备注，业主在小程序能看到
  const openAuditDialog = (row, status) => {
    let remark = '';
    const isReject = status === 'REJECTED';
    const addr = `${row.community || ''} ${row.building || ''} ${row.unit || ''} ${row.house || ''}`.trim();
    Modal.confirm({
      title: isReject ? '驳回业主申请' : '通过业主申请',
      width: 520,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>申请人：<b>{row.name || '-'}</b>（{row.phone || '-'}）</div>
          <div style={{ marginBottom: 8 }}>申请房屋：{addr || '-'}</div>
          <div style={{ marginBottom: 4, marginTop: 12 }}>{isReject ? '驳回原因 / 提示业主正确信息（业主在小程序绑定页可见）' : '备注（可填正确信息，业主在小程序可见，可不填）'}</div>
          <Input.TextArea
            rows={3}
            maxLength={200}
            showCount
            placeholder={isReject ? '例：楼栋号填错了，您的房屋应是 9号楼 1单元 101' : '可选'}
            defaultValue=""
            onChange={(e) => { remark = e.target.value; }}
          />
        </div>
      ),
      okText: isReject ? '确认驳回' : '确认通过',
      okType: isReject ? 'danger' : 'primary',
      cancelText: '取消',
      onOk: async () => {
        if (isReject && (!remark || !remark.trim())) {
          message.warning('驳回请填写原因，方便业主修正');
          return Promise.reject();
        }
        const ok = await handleAudit(row.id, status, (remark || '').trim());
        if (!ok) return Promise.reject();
      }
    });
  };

  // 后台直接添加业主绑定
  const handleBindAdd = async (values) => {
    try {
      const res = await fetch('/api/bind/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          community: values.community,
          building: values.building,
          unit: values.unit,
          house: values.house,
          name: values.name,
          phone: values.phone,
          area: values.area || undefined,
          basement_area: values.basement_area != null && values.basement_area !== '' ? values.basement_area : undefined,
          usage_type: values.usage_type || '住宅',
          has_garage: values.has_garage ? 1 : 0,
          garage_area: values.has_garage ? (values.garage_area || 0) : 0
        })
      });
      const data = await res.json();
      if (res.ok) {
        message.success(data.message || '添加成功');
        setIsBindAddModalVisible(false);
        fetchData();
        fetchStats(); // 刷新工作台绑定统计
      } else {
        message.error(data.error || '添加失败');
      }
    } catch (e) { message.error('请求失败'); }
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const runHouseSearch = (params) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v != null && v !== '') q.set(k, v); });
    setHouseSearchLoading(true);
    fetch('/api/house/search?' + q.toString(), { headers: getAuthHeaders() }).then(async r => {
      const list = await r.json().catch(() => []);
      if (!r.ok) { message.error(list?.error || '搜索失败'); setHouseSearchResults([]); }
      else { setHouseSearchResults(Array.isArray(list) ? list : []); }
    }).catch(() => { message.error('搜索失败'); setHouseSearchResults([]); }).finally(() => setHouseSearchLoading(false));
  };

  const handleHouseEditSave = async () => {
    try {
      const v = await houseEditForm.validateFields();
      const body = {
        id: v.id,
        area: v.area != null && v.area !== '' ? v.area : null,
        basement_area: v.basement_area != null && v.basement_area !== '' ? v.basement_area : null,
        usage_type: v.usage_type || '住宅',
        has_garage: v.has_garage ? 1 : 0,
        garage_area: v.has_garage ? (v.garage_area || 0) : 0
      };
      if (body.area == null && body.basement_area == null && !v.usage_type && !v.has_garage) {
        message.error('请至少填写一项');
        return;
      }
      const res = await fetch('/api/house/update', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok) { message.success(data.message || '保存成功'); setHouseEditModalVisible(false); runHouseSearch(lastSearchParams); }
      else message.error(data.error || '保存失败');
    } catch (e) { if (e.errorFields) message.error('请填写完整'); }
  };

  const handleHouseAdd = async () => {
    try {
      const v = await houseAddForm.validateFields();
      setHouseAddLoading(true);
      const body = {
        community_id: v.community_id,
        building: v.building,
        unit: v.unit,
        room: v.room,
        area: v.area != null && v.area !== '' ? parseFloat(v.area) : undefined,
        basement_area: v.basement_area != null && v.basement_area !== '' ? parseFloat(v.basement_area) : undefined,
        usage_type: v.usage_type || '住宅',
        has_garage: v.has_garage ? 1 : 0,
        garage_area: v.has_garage ? (v.garage_area || 0) : 0
      };
      if (v.owner_name != null && String(v.owner_name).trim() !== '') body.owner_name = String(v.owner_name).trim();
      if (v.owner_phone != null && String(v.owner_phone).trim() !== '') body.owner_phone = String(v.owner_phone).trim();
      const res = await fetch('/api/house/add', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok) { message.success(data.message || '添加成功'); setHouseAddModalVisible(false); houseAddForm.resetFields(); runHouseSearch(lastSearchParams); }
      else message.error(data.error || '添加失败');
    } catch (e) { if (e.errorFields) message.error('请填写完整'); }
    finally { setHouseAddLoading(false); }
  };

  const getMenuItems = () => [
    { key: 'dashboard', icon: <DashboardOutlined />, label: '工作台' },
    { key: 'communities', icon: <HomeOutlined />, label: '小区管理' },
    { key: 'houses', icon: <SearchOutlined />, label: '房屋管理' },
    { key: 'audit', icon: <UserOutlined />, label: '业主审核' },
    { key: 'repairs', icon: <ToolOutlined />, label: '报修管理' },
    { key: 'notices', icon: <NotificationOutlined />, label: '公告管理' },
    { key: 'menus', icon: <AppstoreOutlined />, label: '菜单配置' },
    { key: 'butler', icon: <PhoneOutlined />, label: '管家设置' },
    { key: 'bills', icon: <LaptopOutlined />, label: '账单管理' },
    { key: 'parking', icon: <HomeOutlined />, label: '车位管理' },
    { key: 'parkingBills', icon: <LaptopOutlined />, label: '停车费管理' },
    { key: 'pointsPrize', icon: <AppstoreOutlined />, label: '积分奖品' },
    { key: 'pointsOrders', icon: <NotificationOutlined />, label: '兑换订单' },
    { key: 'pointsManage', icon: <LaptopOutlined />, label: '积分管理' },
    { key: 'pointsConfig', icon: <ToolOutlined />, label: '积分配置' },
    { key: 'feedback', icon: <EditOutlined />, label: '投诉建议' },
  ];

  const auditColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '业主姓名', dataIndex: 'name' },
    { title: '手机号', dataIndex: 'phone' },
    { 
      title: '申请房屋', 
      render: (_, r) => `${r.community} ${r.building} ${r.unit} ${r.house}`
    },
    { title: '面积(平米)', dataIndex: 'area', width: 90, render: v => (v != null && v !== '') ? v : '-' },
    { title: '房屋用途', dataIndex: 'usage_type', width: 80, render: formatUsageType },
    { title: '有无车库', dataIndex: 'has_garage', width: 80, render: formatHasGarage },
    { title: '车库面积(㎡)', dataIndex: 'garage_area', width: 100, render: v => Number(v) > 0 ? v : '-' },
    { title: '申请时间', dataIndex: 'create_time', render: formatDateTime },
    { 
      title: '状态', 
      dataIndex: 'status',
      width: 90,
      render: t => {
        let color = 'blue';
        let text = '审核中';
        if (t === 'APPROVED') { color = 'green'; text = '已通过'; }
        if (t === 'REJECTED') { color = 'red'; text = '已驳回'; }
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: '审核备注',
      dataIndex: 'audit_remark',
      width: 220,
      render: (t) => t ? <span style={{ color: '#d4380d', whiteSpace: 'pre-wrap' }}>{t}</span> : <span style={{ color: '#bbb' }}>-</span>
    },
    { 
      title: '操作', 
      width: 300,
      render: (_, r) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleAuditEdit(r)}>编辑</Button>
          {r.status === 'PENDING' && (
            <>
              <Button type="primary" size="small" icon={<CheckCircleOutlined />} onClick={() => openAuditDialog(r, 'APPROVED')}>通过</Button>
              <Button danger size="small" icon={<CloseCircleOutlined />} onClick={() => openAuditDialog(r, 'REJECTED')}>驳回</Button>
            </>
          )}
          {currentUser?.role === 'SUPER_ADMIN' && (
            <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteAuditApply(r)}>删除</Button>
          )}
        </Space>
      )
    }
  ];

  const parkingColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '小区', dataIndex: 'community_name', width: 120 },
    { title: '车位编号', dataIndex: 'code', width: 100 },
    { title: '绑定手机', dataIndex: 'phone', width: 120 },
    { title: '状态', dataIndex: 'status', width: 90, render: t => t === 'AVAILABLE' ? '空闲' : t === 'OWNED' ? '已售' : t === 'RENTED' ? '已租' : t },
  ];
  const parkingBillColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '小区', dataIndex: 'community_name', width: 120 },
    { title: '车位', dataIndex: 'space_code', width: 90 },
    { title: '金额', dataIndex: 'amount', width: 90, render: v => v != null ? '¥' + Number(v).toFixed(2) : '-' },
    { title: '账期', dataIndex: 'month', width: 210, render: (v, r) => formatParkingBillPeriod(v, r.bill_type) },
    { title: '类型', dataIndex: 'bill_type', width: 80 },
    { title: '状态', dataIndex: 'status', width: 80, render: t => <Tag color={t === 'PAID' ? 'green' : 'orange'}>{t === 'PAID' ? '已缴' : '未缴'}</Tag> },
    { title: '操作', width: 140, render: (_, r) => <Space><Button type="link" size="small" danger onClick={() => deleteParkingBill(r)}>删除</Button>{r.status === 'UNPAID' ? <Button type="link" size="small" onClick={() => markParkingBillPaid(r.id)}>标记已缴</Button> : null}</Space> },
  ];

  const POINTS_CATEGORIES = [{ label: '物业服务', value: 'service' }, { label: '实物礼品', value: 'gift' }, { label: '社区活动', value: 'activity' }];
  const pointsPrizeColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '名称', dataIndex: 'name', width: 140 },
    { title: '分类', dataIndex: 'category', width: 100, render: c => (POINTS_CATEGORIES.find(o => o.value === c) || {}).label || c },
    { title: '所需积分', dataIndex: 'points_required', width: 90 },
    { title: '库存', dataIndex: 'stock', width: 70 },
    { title: '有效期', dataIndex: 'valid_until', width: 110, render: v => (v && String(v).match(/^(\d{4}-\d{2}-\d{2})/)) ? String(v).slice(0, 10) : (v || '-') },
    { title: '上架', dataIndex: 'is_online', width: 70, render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '是' : '否'}</Tag> },
    { title: '操作', width: 160, render: (_, r) => <Space><Button type="link" size="small" onClick={() => { setEditingPointsPrizeId(r.id); pointsPrizeForm.setFieldsValue({ ...r, is_online: !!r.is_online }); setPointsPrizeFileList(r.image_url ? [{ uid: -1, name: 'image.png', status: 'done', url: r.image_url }] : []); setIsPointsPrizeModalVisible(true); }}>编辑</Button><Button type="link" size="small" danger onClick={() => { Modal.confirm({ title: '确认删除', content: '删除后不可恢复，确定吗？', okText: '删除', okType: 'danger', cancelText: '取消', onOk: () => fetch('/api/points/admin/prize/delete', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ id: r.id }) }).then(res => { const ok = res.ok; return res.json().then(d => { if (ok) { message.success('已删除'); fetchData(); } else message.error(d.error || '删除失败'); }); }) }); }}>删除</Button></Space> },
  ];
  const pointsOrderColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '手机号', dataIndex: 'phone', width: 110 },
    { title: '奖品', dataIndex: 'prize_name', width: 140 },
    { title: '积分', dataIndex: 'points', width: 70 },
    { title: '申请时间', dataIndex: 'create_time', width: 165, render: t => t ? String(t).replace('T', ' ').slice(0, 19) : '-' },
    { title: '审核时间', dataIndex: 'audit_time', width: 165, render: t => t ? String(t).replace('T', ' ').slice(0, 19) : '-' },
    { title: '发放时间', dataIndex: 'ship_time', width: 165, render: t => t ? String(t).replace('T', ' ').slice(0, 19) : '-' },
    { title: '状态', dataIndex: 'status', width: 90, render: s => <Tag color={s === 'SHIPPED' ? 'green' : s === 'APPROVED' ? 'blue' : s === 'REJECTED' ? 'red' : 'orange'}>{s === 'PENDING' ? '待审核' : s === 'APPROVED' ? '已通过' : s === 'REJECTED' ? '已驳回' : '已发放'}</Tag> },
    { title: '驳回原因', dataIndex: 'reject_reason', width: 140, render: t => t || '-' },
    { title: '操作', width: 140, render: (_, r) => r.status === 'PENDING' ? <Space><Button type="link" size="small" onClick={() => { setAuditingOrderId(r.id); setPointsAuditRejectReason(''); setIsPointsOrderAuditVisible(true); }}>审核</Button></Space> : r.status === 'APPROVED' ? <Button type="link" size="small" onClick={() => fetch('/api/points/admin/order/ship', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ id: r.id }) }).then(async res => { const d = await res.json(); if (res.ok) { message.success('已标记发放'); fetchData(); } else message.error(d.error || '操作失败'); })}>标记发放</Button> : null },
  ];

  const handlePointsPrizeSubmit = async () => {
    try {
      const v = await pointsPrizeForm.validateFields();
      setPointsPrizeSubmitting(true);
      const body = { name: v.name, category: v.category || 'service', points_required: v.points_required || 0, image_url: v.image_url, description: v.description, stock: v.stock || 0, valid_until: v.valid_until || null, is_online: v.is_online ? 1 : 0, sort_order: v.sort_order || 0 };
      const url = editingPointsPrizeId ? '/api/points/admin/prize/update' : '/api/points/admin/prize/add';
      if (editingPointsPrizeId) body.id = editingPointsPrizeId;
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok) { message.success(data.message || '保存成功'); setIsPointsPrizeModalVisible(false); pointsPrizeForm.resetFields(); setEditingPointsPrizeId(null); setPointsPrizeFileList([]); fetchData(); }
      else message.error(data.error || '保存失败');
    } catch (e) { if (!e.errorFields) message.error('保存失败'); }
    finally { setPointsPrizeSubmitting(false); }
  };
  const handlePointsOrderAudit = async (status) => {
    if (status === 'REJECTED' && !pointsAuditRejectReason.trim()) { message.error('驳回需填写原因'); return; }
    try {
      const res = await fetch('/api/points/admin/order/audit', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ id: auditingOrderId, status, reject_reason: pointsAuditRejectReason }) });
      const data = await res.json();
      if (res.ok) { message.success(data.message); setIsPointsOrderAuditVisible(false); setAuditingOrderId(null); fetchData(); }
      else message.error(data.error || '操作失败');
    } catch (e) { message.error('操作失败'); }
  };
  const handlePointsAdjust = async () => {
    try {
      const v = await pointsAdjustForm.validateFields();
      setPointsAdjustSubmitting(true);
      const res = await fetch('/api/points/admin/points/adjust', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ phone: v.phone, amount: v.amount, remark: v.remark }) });
      const data = await res.json();
      if (res.ok) { message.success(data.message); pointsAdjustForm.resetFields(); fetchData(); }
      else message.error(data.error || '操作失败');
    } catch (e) { if (!e.errorFields) message.error('请填写完整'); }
    finally { setPointsAdjustSubmitting(false); }
  };
  const handlePointsConfigSave = async () => {
    let v;
    try {
      v = await pointsConfigForm.validateFields();
    } catch (e) {
      if (e && e.errorFields && e.errorFields.length) {
        message.error('请检查表单：' + (e.errorFields[0].errors && e.errorFields[0].errors[0] || '有字段未填'));
      } else {
        message.error('请填写完整');
      }
      return;
    }
    try {
      setPointsConfigSubmitting(true);
      const payload = { ...v, clear_enabled: v.clear_enabled ? 1 : 0 };
      const res = await fetch('/api/points/admin/config', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { message.success(data.message || '保存成功'); fetchData(); }
      else message.error(data.error || `保存失败（HTTP ${res.status}）`);
    } catch (e) {
      message.error('网络异常：' + (e && e.message || '请求失败'));
    } finally {
      setPointsConfigSubmitting(false);
    }
  };

  const handleParkingSpaceAdd = async (values) => {
    try {
      const res = await fetch('/api/parking/space/add', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(values) });
      const data = await res.json();
      if (res.ok) { message.success(data.message || '添加成功'); setIsParkingSpaceModalVisible(false); parkingSpaceForm.resetFields(); fetchData(); }
      else message.error(data.error || '添加失败');
    } catch (e) { message.error('请求失败'); }
  };
  const handleParkingBillAdd = async (values) => {
    setParkingBillSubmitLoading(true);
    try {
      const billType = values.bill_type || '月租';
      const body = {
        community_id: values.community_id,
        parking_space_id: values.parking_space_id,
        amount: values.amount,
        bill_type: billType,
        month: buildParkingBillPeriod(values.parking_start_date, values.parking_end_date, billType)
      };
      const res = await fetch('/api/parking/bill/add', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok) { message.success(data.message || '添加成功'); setIsParkingBillModalVisible(false); parkingBillForm.resetFields(); setParkingBillSpaceList([]); fetchData(); }
      else message.error(data.error || '添加失败');
    } catch (e) { message.error('请求失败'); }
    finally { setParkingBillSubmitLoading(false); }
  };
  const loadParkingSpacesForBill = async (communityId) => {
    if (!communityId) {
      setParkingBillSpaceList([]);
      return;
    }
    try {
      const res = await fetch('/api/parking/space/list?community_id=' + communityId, { headers: getAuthHeaders() });
      const data = await res.json();
      setParkingBillSpaceList(Array.isArray(data) ? data : []);
    } catch (e) {
      setParkingBillSpaceList([]);
      message.error('获取车位列表失败');
    }
  };
  const markParkingBillPaid = async (id) => {
    try {
      const res = await fetch('/api/parking/bill/mark-paid', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ id }) });
      const data = await res.json();
      if (res.ok) { message.success('已标记为已缴'); fetchData(); }
      else message.error(data.error || '操作失败');
    } catch (e) { message.error('操作失败'); }
  };
  const deleteParkingBill = (r) => {
    Modal.confirm({ title: '确认删除', content: `确定删除该停车费账单（ID: ${r.id}）吗？`, okText: '删除', okType: 'danger', cancelText: '取消', onOk: async () => {
      try {
        const res = await fetch('/api/parking/bill/delete', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ id: r.id }) });
        const data = await res.json();
        if (res.ok) { message.success('删除成功'); fetchData(); }
        else message.error(data.error || '删除失败');
      } catch (e) { message.error('删除失败'); }
    } });
  };

  const updateBillStatusLocal = (id, newStatus) => {
    setData(prev => prev.map(item => item.id === id ? { ...item, status: newStatus } : item));
    setFilteredData(prev => prev.map(item => item.id === id ? { ...item, status: newStatus } : item));
  };

  const markBillPaid = async (id) => {
    try {
      const res = await fetch('/api/bill/pay', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ id }) });
      const data = await res.json();
      if (res.ok) { message.success('已标记为已缴'); fetchData(); }
      else message.error(data.error || '操作失败');
    } catch (e) { message.error('操作失败'); }
  };

  const markBillUnpaid = async (id) => {
    Modal.confirm({
      title: '改回未缴？',
      content: '操作失误时可改回未缴，请确认',
      onOk: async () => {
        try {
          const res = await fetch('/api/bill/pay', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ id, undo: true }) });
          const data = await res.json();
          if (res.ok) { message.success('已改回未缴'); fetchData(); }
          else {
            const isApiNotFound = (res.status === 404 && (data?.error === 'API not found' || (data?.error && String(data.error).includes('not found'))));
            const errMsg = isApiNotFound
              ? '接口未找到：请上传最新 server.js 到服务器并重启 Node 项目'
              : (data?.error || '操作失败');
            message.error(errMsg);
          }
        } catch (e) { message.error('操作失败'); }
      }
    });
  };

  const billColumns = [
    { title: 'ID', dataIndex: 'id' },
    { title: '类型', dataIndex: 'bill_type', render: t => t || '物业费' },
    { 
      title: '房屋信息', 
      render: (_, r) => {
        if (!r.building && !r.community_name) return `房屋ID: ${r.house_id}`;
        const addr = `${r.community_name || ''} ${r.building || ''} ${r.unit || ''} ${r.room || ''}`.trim();
        return (
          <div>
            <div>{addr}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{formatUsageType(r.usage_type)} · 车库{formatHasGarage(r.has_garage)}</div>
          </div>
        );
      }
    },
    { title: '金额', dataIndex: 'amount', render: t => `¥${t}` },
    { title: '月份', dataIndex: 'month' },
    { title: '状态', render: (_, r) => (
      <Space size={4} wrap>
        <Tag color={r.status === 'PAID' ? 'green' : 'red'}>{r.status === 'PAID' ? '已缴' : '未缴'}</Tag>
        {r.status !== 'PAID' && r.pay_enabled === false ? <Tag>未到缴费时间</Tag> : null}
      </Space>
    ) },
    { title: '缴费时间', dataIndex: 'pay_time', width: 150, render: formatDateTime },
    { title: '操作', render: (_, r) => (
      <Space>
        {r.status !== 'PAID' && r.pay_enabled !== false && <Button type="link" size="small" onClick={() => markBillPaid(r.id)}>标记已缴</Button>}
        {r.status === 'PAID' && <Button type="link" size="small" onClick={() => markBillUnpaid(r.id)}>改回未缴</Button>}
        {currentUser?.role === 'SUPER_ADMIN' && <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => deleteItem(r.id, 'bill')}>删除</Button>}
      </Space>
    ) }
  ];

  const repairColumns = [
    { title: 'ID', dataIndex: 'id' },
    { 
      title: '房屋信息', 
      render: (_, r) => {
        if (!r.building) return `房屋ID: ${r.house_id}`;
        return (
          <div style={{ fontSize: '12px' }}>
            <div style={{ fontWeight: 'bold' }}>{r.community_name || '未知小区'}</div>
            <div>{r.building} {r.unit} {r.room}</div>
          </div>
        );
      }
    },
    { title: '类型', dataIndex: 'type' },
    { title: '内容', dataIndex: 'content' },
    { 
      title: '图片', 
      dataIndex: 'images', 
      render: (t) => {
        try {
          const imgs = JSON.parse(t || '[]');
          if (!imgs || imgs.length === 0) return '无图';
          return (
            <div style={{ display: 'flex', gap: '8px' }}>
              {imgs.map((url, i) => {
                let safeUrl = url || '';
                if (safeUrl && (safeUrl.startsWith('http://') || safeUrl.startsWith('https://'))) {
                  try { safeUrl = new URL(url).pathname; } catch (_) { /* 保持原样 */ }
                }
                return <img key={i} src={safeUrl} alt="图" style={{ width: 50, height: 50, objectFit: 'cover', cursor: 'pointer', borderRadius: 4 }} onClick={() => window.open(safeUrl, '_blank')} />;
              })}
            </div>
          );
        } catch (e) { return '无图'; }
      }
    },
    { title: '状态', dataIndex: 'status', width: 96, render: t => {
      const map = { DONE: { c: 'green', t: '已处理' }, PROCESSING: { c: 'blue', t: '处理中' } };
      const s = map[t] || { c: 'orange', t: '未处理' };
      return <Tag color={s.c}>{s.t}</Tag>;
    } },
    { title: '负责人', dataIndex: 'handler', width: 90, render: t => t || <span style={{ color: '#bbb' }}>未派单</span> },
    { title: '处理时长', width: 120, render: (_, r) => formatDuration(r.create_time, r.done_time) },
    { title: '操作', render: (_, r) => (
      <Space>
        {r.status !== 'DONE' && <Button type="link" size="small" onClick={() => openAssignModal(r)}>{r.handler ? '改派' : '派单'}</Button>}
        {r.status !== 'DONE' && <Button type="link" size="small" onClick={() => updateRepairStatus(r.id, 'DONE')}>标记已处理</Button>}
        {currentUser?.role === 'SUPER_ADMIN' && <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => deleteItem(r.id, 'repair')}>删除</Button>}
      </Space>
    )}
  ];

  const noticeColumns = [
    { 
      title: '置顶', 
      dataIndex: 'is_top', 
      width: 80,
      render: (t, r) => (
        <Button 
          type={t ? 'primary' : 'default'} 
          size="small" 
          icon={<PushpinOutlined />} 
          onClick={() => toggleNoticeTop(r.id, t)}
        />
      )
    },
    { title: '标题', dataIndex: 'title', width: 140 },
    { title: '内容', dataIndex: 'content', ellipsis: true, render: (t) => t ? (t.length > 40 ? t.slice(0, 40) + '…' : t) : '-' },
    { 
      title: '图片', 
      dataIndex: 'image_url', 
      render: (t) => {
        if (!t) return '-';
        let src = t;
        if (t.startsWith('http://') || t.startsWith('https://')) {
          try { src = new URL(t).pathname; } catch (_) { /* 保持原样 */ }
        }
        return <img src={src} alt="图" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />;
      }
    },
    { title: '阅读量', dataIndex: 'views', render: t => <Tag color="blue">{t || 0}</Tag> },
    { title: '发布时间', dataIndex: 'create_time', render: formatDateTime },
    { title: '操作', render: (_, r) => (
      <Space>
        <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
        {currentUser?.role === 'SUPER_ADMIN' && <Button type="link" danger icon={<DeleteOutlined />} onClick={() => deleteItem(r.id, 'notice')}>删除</Button>}
      </Space>
    )}
  ];

  const communityColumns = [
    { title: 'ID', dataIndex: 'id' },
    { title: '小区名称', dataIndex: 'name' },
    { title: '地址', dataIndex: 'address' },
    { title: '配套车库', dataIndex: 'has_garage_facility', width: 90, render: v => <Tag color={Number(v) === 1 ? 'green' : 'default'}>{Number(v) === 1 ? '是' : '否'}</Tag> },
    { title: '车库单价(元/㎡/年)', dataIndex: 'garage_unit_price', width: 130, render: v => Number(v) > 0 ? v : '-' },
    { title: '操作', render: (_, r) => <Space><Button type="link" onClick={() => openEdit(r)}>编辑</Button>{currentUser?.role === 'SUPER_ADMIN' && <Button type="link" danger onClick={() => deleteItem(r.id, 'community')}>删除</Button>}</Space> },
  ];

  const markFeedbackDone = async (id) => {
    try {
      const res = await fetch('/api/feedback/mark-done', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ id }) });
      const data = await res.json();
      if (res.ok) { message.success('已标记为已处理'); fetchData(); }
      else message.error(data.error || '操作失败');
    } catch (e) { message.error('操作失败'); }
  };

  const feedbackColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '状态', dataIndex: 'status', width: 88, render: t => <Tag color={(t || 'PENDING') === 'DONE' ? 'green' : 'orange'}>{(t || 'PENDING') === 'DONE' ? '已处理' : '未处理'}</Tag> },
    {
      title: '提交人信息',
      key: 'submitter',
      width: 220,
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div><strong>手机</strong> {r.phone || '-'}</div>
          <div style={{ color: '#666', marginTop: 4 }}>{[r.community, r.building, r.unit, r.house].filter(Boolean).join(' ') || '-'}</div>
        </div>
      )
    },
    { title: '完整内容', dataIndex: 'content', ellipsis: false, render: (t) => t ? <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxWidth: 400 }}>{t}</div> : '-' },
    { title: '提交时间', dataIndex: 'create_time', width: 170, render: formatDateTime },
    { title: '操作', width: 100, render: (_, r) => (r.status || 'PENDING') !== 'DONE' && <Button type="link" size="small" onClick={() => markFeedbackDone(r.id)}>标记已处理</Button> },
  ];

  const houseSearchColumns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '小区', dataIndex: 'community_name' },
    { title: '楼栋', dataIndex: 'building', width: 90 },
    { title: '单元', dataIndex: 'unit', width: 80 },
    { title: '房号', dataIndex: 'room', width: 80 },
    { title: '房屋用途', dataIndex: 'usage_type', width: 80, render: formatUsageType },
    { title: '有无车库', dataIndex: 'has_garage', width: 80, render: formatHasGarage },
    { title: '车库面积(㎡)', dataIndex: 'garage_area', width: 100, render: v => Number(v) > 0 ? v : '-' },
    { title: '面积(㎡)', dataIndex: 'area', width: 90, render: v => (v != null && v !== '') ? v : '-' },
    { title: '地下室面积(㎡)', dataIndex: 'basement_area', width: 110, render: v => (v != null && v !== '') ? v : '-' },
    { title: '业主姓名', dataIndex: 'owner_name', width: 90, render: v => (v != null && v !== '') ? v : '-' },
    { title: '手机号', dataIndex: 'owner_phone', width: 110, render: v => (v != null && v !== '') ? v : '-' },
    { title: '创建时间', dataIndex: 'create_time', width: 160, render: formatDateTime },
    { title: '操作', width: 150, fixed: 'right', render: (_, r) => (
      <Space size={4}>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { houseEditForm.setFieldsValue({ id: r.id, community_id: r.community_id, community_name: r.community_name, building: r.building, unit: r.unit, room: r.room, area: r.area, basement_area: r.basement_area, usage_type: r.usage_type || '住宅', has_garage: Number(r.has_garage) === 1, garage_area: r.garage_area || 0, community_has_garage_facility: Number(r.community_has_garage_facility) === 1 }); setHouseEditModalVisible(true); }}>编辑</Button>
        {currentUser?.role === 'SUPER_ADMIN' && <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteHouse(r)}>删除</Button>}
      </Space>
    ) }
  ];

  const menuColumns = [
    { title: '排序', dataIndex: 'sort_order', width: 80 },
    { title: '图标', dataIndex: 'icon', render: t => <span style={{fontSize: '24px'}}>{t}</span> },
    { title: '名称', dataIndex: 'name' },
    { title: '跳转路径', dataIndex: 'page_path' },
    { title: '状态', dataIndex: 'is_visible', render: t => <Tag color={t===1?'green':'red'}>{t===1?'显示':'隐藏'}</Tag> },
    { title: '操作', render: (_, r) => (
      <Space>
        <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
        {currentUser?.role === 'SUPER_ADMIN' && <Button type="link" danger icon={<DeleteOutlined />} onClick={() => deleteItem(r.id, 'menu')}>删除</Button>}
      </Space>
    )}
  ];

  if (initLoading) return <div style={{height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>加载中...</div>;

  if (!isLoggedIn) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24
      }}>
        <Card
          title={<span style={{ fontSize: 18, fontWeight: 600 }}>德易居物业后台</span>}
          style={{ width: 360, borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' }}
          bodyStyle={{ padding: '24px 24px 32px' }}
        >
          <div style={{ color: '#666', marginBottom: 20, fontSize: 13 }}>请使用管理员账号登录</div>
          <Form form={loginForm} onFinish={handleLogin} size="large">
            <Form.Item name="username" rules={[{ required: true, message: '请输入账号' }]}>
              <Input prefix={<DashboardOutlined style={{ color: '#999' }} />} placeholder="账号" />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password prefix={<LaptopOutlined style={{ color: '#999' }} />} placeholder="密码" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" block size="large" style={{ height: 44 }}>登录</Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header className="header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ color: 'white', fontSize: '20px' }}>德易居物业后台</div>
          <Space>
            <Button type="link" style={{ color: 'white' }} icon={<LockOutlined />} onClick={() => {
              pwdForm.resetFields();
              setIsPwdModalVisible(true);
            }}>修改密码</Button>
            <Button type="link" style={{ color: 'white' }} onClick={handleLogout}>退出登录</Button>
          </Space>
        </div>
      </Header>
      <Layout>
        <Sider width={200} style={{ background: '#fff' }}>
          <Menu
            mode="inline"
            selectedKeys={[activeTab]}
            style={{ height: '100%', borderRight: 0 }}
            onClick={(e) => { setSearchText(''); navigate(`/${e.key}`); }}
            items={getMenuItems()}
          />
        </Sider>
        <Layout style={{ padding: '24px' }}>
          <Content style={{ padding: 24, background: '#fff' }}>
            {activeTab === 'dashboard' ? (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Card hoverable onClick={() => navigate('/feedback')} style={{ borderRadius: 8 }} bodyStyle={{ padding: 20, position: 'relative' }}>
                    {stats.hasNewFeedback && <span style={{ position: 'absolute', top: 10, right: 10, color: '#FF3B30', fontSize: 18, lineHeight: 1 }}>❗</span>}
                    <Statistic title="投诉管理" value={`今日：${stats.feedbackToday || 0} / 待处理：${stats.feedbackTotal || 0}`} valueStyle={{ fontSize: 20 }} />
                    <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>点击进入投诉建议</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card hoverable onClick={() => navigate('/repairs')} style={{ borderRadius: 8 }} bodyStyle={{ padding: 20, position: 'relative' }}>
                    {stats.hasNewRepair && <span style={{ position: 'absolute', top: 10, right: 10, color: '#FF3B30', fontSize: 18, lineHeight: 1 }}>❗</span>}
                    <Statistic title="报修管理" value={`今日：${stats.repairToday || 0} / 待派单：${stats.pendingRepairs || 0}`} valueStyle={{ fontSize: 20 }} />
                    <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>点击进入报修管理</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card hoverable onClick={() => navigate('/audit')} style={{ borderRadius: 8 }} bodyStyle={{ padding: 20, position: 'relative' }}>
                    {stats.hasNewAudit && <span style={{ position: 'absolute', top: 10, right: 10, color: '#FF3B30', fontSize: 18, lineHeight: 1 }}>❗</span>}
                    <Statistic title="业主审核" value={`待审核：${stats.auditPending || 0} / 已通过：${stats.approvedBindCount || 0}`} valueStyle={{ fontSize: 20 }} />
                    <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>点击进入业主审核</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card hoverable onClick={() => navigate('/bills')} style={{ borderRadius: 8 }} bodyStyle={{ padding: 20 }}>
                    <Statistic title="未缴账单总额" value={stats.unpaidAmount} precision={2} prefix="¥" valueStyle={{ color: '#cf1322' }} />
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>点击进入账单管理</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card hoverable onClick={() => navigate('/bills')} style={{ borderRadius: 8 }} bodyStyle={{ padding: 20 }}>
                    <Statistic title="未缴账单数" value={stats.unpaidBills} suffix="笔" />
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>点击进入账单管理</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card hoverable onClick={() => navigate('/notices')} style={{ borderRadius: 8 }} bodyStyle={{ padding: 20 }}>
                    <Statistic title="已发公告" value={stats.totalNotices} prefix={<NotificationOutlined style={{ color: '#1890ff' }} />} />
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>点击进入公告管理</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card hoverable onClick={() => navigate('/audit')} style={{ borderRadius: 8 }} bodyStyle={{ padding: 20 }}>
                    <Statistic title="已绑定房屋" value={stats.totalHouses > 0 ? stats.boundHouses : (stats.approvedBindCount || 0)} suffix={stats.totalHouses > 0 ? `/ ${stats.totalHouses}` : ' 户已通过审核'} prefix={<UserOutlined style={{ color: '#52c41a' }} />} />
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{stats.totalHouses > 0 ? `未绑定 ${stats.unboundHouses || 0} 户 · ` : ''}点击进入业主审核</div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card hoverable onClick={() => navigate('/bills')} style={{ borderRadius: 8 }} bodyStyle={{ padding: 20 }}>
                    <Statistic title="已缴费人数" value={stats.paidHouseCount} suffix="户" valueStyle={{ color: '#52c41a' }} />
                    <Statistic title="已缴费总额" value={stats.paidTotalAmount != null ? stats.paidTotalAmount : 0} precision={2} prefix="¥" valueStyle={{ fontSize: 18, color: '#52c41a', marginTop: 8 }} />
                    <Statistic title="其中车库费" value={stats.garagePaidAmount != null ? stats.garagePaidAmount : 0} precision={2} prefix="¥" valueStyle={{ fontSize: 14, color: '#722ed1', marginTop: 4 }} />
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>点击进入账单管理</div>
                  </Card>
                </Col>
                <Col span={24}>
                  <Card title="运营数据看板（近 30 天）" style={{ borderRadius: 8 }}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={8} md={6}>
                        <div style={{ textAlign: 'center' }}>
                          <Progress type="dashboard" percent={trend ? trend.payRate : 0} size={120} />
                          <div style={{ marginTop: 8, color: '#666' }}>缴费率</div>
                          <div style={{ fontSize: 12, color: '#999' }}>
                            已缴 {trend ? trend.paidHouses : 0} / 已绑定 {trend ? trend.boundHouses : 0} 户
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} sm={16} md={6}>
                        <Statistic title="报修平均处理时长" value={trend && trend.avgRepairMinutes != null ? formatDuration(0, trend.avgRepairMinutes * 60000) : '暂无数据'} />
                        <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>基于 {trend ? trend.doneRepairCount : 0} 单已完成报修</div>
                      </Col>
                      <Col xs={24} md={12}>
                        <Table
                          size="small"
                          rowKey="date"
                          pagination={{ pageSize: 7, showSizeChanger: false, size: 'small' }}
                          dataSource={trend && Array.isArray(trend.trend) ? [...trend.trend].reverse() : []}
                          locale={{ emptyText: '暂无趋势数据' }}
                          columns={[
                            { title: '日期', dataIndex: 'date', width: 110 },
                            { title: '缴费额', dataIndex: 'pay_amount', render: v => `¥${Number(v || 0).toFixed(2)}` },
                            { title: '缴费笔数', dataIndex: 'pay_count' },
                            { title: '报修', dataIndex: 'repair_count' },
                            { title: '投诉', dataIndex: 'feedback_count' }
                          ]}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={24}>
                  <Card
                    title={
                      <Space size="middle" wrap>
                        <span>缴费账单历史记录</span>
                        <Tag color="green">今日缴费 {paidHistorySummary.todayCount} 笔 · ¥{Number(paidHistorySummary.todayAmount || 0).toFixed(2)}</Tag>
                        <Tag color="blue">当前筛选 {paidHistorySummary.total} 笔 · 合计 ¥{Number(paidHistorySummary.totalAmount || 0).toFixed(2)}</Tag>
                        <Tag color="purple">其中车库费 {paidHistorySummary.garageCount || 0} 笔 · ¥{Number(paidHistorySummary.garageAmount || 0).toFixed(2)}</Tag>
                      </Space>
                    }
                    extra={
                      <Space wrap>
                        <Segmented
                          value={paidHistoryFilter.quickDay || 'all'}
                          onChange={val => fetchPaidHistory({ quickDay: val === 'all' ? '' : val, range: null, page: 1 })}
                          options={[
                            { label: '全部', value: 'all' },
                            { label: '今日', value: 'today' },
                            { label: '昨日', value: 'yesterday' },
                            { label: '前日', value: 'day-before-yesterday' }
                          ]}
                        />
                        <DatePicker.RangePicker
                          value={paidHistoryFilter.range}
                          disabled={!!paidHistoryFilter.quickDay}
                          onChange={val => fetchPaidHistory({ range: val, quickDay: '', page: 1 })}
                          allowClear
                        />
                        <Select
                          placeholder="费用类型"
                          allowClear
                          style={{ width: 140 }}
                          value={paidHistoryFilter.billType || undefined}
                          onChange={val => fetchPaidHistory({ billType: val || '', page: 1 })}
                          options={[
                            { value: '物业费', label: '物业费' },
                            { value: '公用电费', label: '公用电费' },
                            { value: '城市垃圾处理费', label: '城市垃圾处理费' },
                            { value: '车库费', label: '车库费' },
                            { value: '停车费', label: '停车费' }
                          ]}
                        />
                        <Input.Search
                          placeholder="搜小区/楼栋/房号/姓名/手机"
                          style={{ width: 220 }}
                          allowClear
                          value={paidHistoryFilter.keyword}
                          onChange={e => setPaidHistoryFilter(f => ({ ...f, keyword: e.target.value }))}
                          onSearch={val => fetchPaidHistory({ keyword: val || '', page: 1 })}
                        />
                        <span style={{ fontSize: 12, color: '#666' }}>
                          <Switch
                            size="small"
                            checked={paidHistoryFilter.includeParking}
                            onChange={val => fetchPaidHistory({ includeParking: val, page: 1 })}
                          /> 含停车费
                        </span>
                        <Button icon={<DownloadOutlined />} onClick={async () => {
                          try {
                            const params = new URLSearchParams();
                            if (paidHistoryFilter.includeParking) params.set('includeParking', '1');
                            const quickRange = resolveQuickDayRange(paidHistoryFilter.quickDay);
                            if (quickRange) {
                              params.set('dateFrom', quickRange.from);
                              params.set('dateTo', quickRange.to);
                            } else {
                              if (paidHistoryFilter.range && paidHistoryFilter.range[0]) params.set('dateFrom', paidHistoryFilter.range[0].format('YYYY-MM-DD'));
                              if (paidHistoryFilter.range && paidHistoryFilter.range[1]) params.set('dateTo', paidHistoryFilter.range[1].format('YYYY-MM-DD'));
                            }
                            if (paidHistoryFilter.billType) params.set('billType', paidHistoryFilter.billType);
                            if (paidHistoryFilter.keyword) params.set('keyword', paidHistoryFilter.keyword);
                            const res = await fetch('/api/bill/paid-export?' + params.toString(), { headers: getAuthHeaders() });
                            if (!res.ok) { message.error('导出失败'); return; }
                            const blob = await res.blob();
                            const link = document.createElement('a');
                            link.href = URL.createObjectURL(blob);
                            link.download = `缴费历史_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
                            link.click();
                            URL.revokeObjectURL(link.href);
                            message.success('已导出');
                          } catch (e) { message.error('导出失败：' + (e.message || '')); }
                        }}>导出账单流水</Button>
                        <Button onClick={() => fetchPaidHistory({ page: paidHistoryFilter.page })}>刷新</Button>
                      </Space>
                    }
                  >
                    <Table
                      size="small"
                      rowKey="row_key"
                      loading={paidHistoryLoading}
                      dataSource={paidHistoryData}
                      rowClassName={r => r.is_today ? 'paid-row-today' : ''}
                      columns={[
                        { title: '缴费时间', dataIndex: 'pay_time', width: 165, render: t => t ? formatDateTime(t) : '-' },
                        {
                          title: '业主 / 房屋',
                          render: (_, r) => {
                            const name = (r.owner_name || '').trim();
                            const parts = [];
                            if (r.community_name) parts.push(r.community_name);
                            if (r.building) parts.push(/号楼|栋|号/.test(r.building) ? r.building : r.building + '号楼');
                            if (r.unit) parts.push(/单元/.test(r.unit) ? r.unit : r.unit + '单元');
                            if (r.room) parts.push(/室|号/.test(r.room) ? r.room : r.room + '室');
                            const addr = parts.join(' ') || '-';
                            return <span>{name ? <b>{name}</b> : <span style={{ color: '#999' }}>未绑定</span>} <span style={{ color: '#666' }}>／ {addr}</span></span>;
                          }
                        },
                        { title: '手机号', dataIndex: 'owner_phone', width: 120, render: t => t || '-' },
                        { title: '类型', dataIndex: 'bill_type', width: 130, render: t => <Tag color={String(t || '').includes('停车') ? 'purple' : String(t || '') === '车库费' ? 'gold' : 'geekblue'}>{t || '物业费'}</Tag> },
                        { title: '房屋用途', dataIndex: 'usage_type', width: 80, render: formatUsageType },
                        { title: '车库', dataIndex: 'has_garage', width: 60, render: v => formatHasGarage(v) },
                        { title: '金额', dataIndex: 'amount', width: 100, sorter: (a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0), render: v => <b style={{ color: '#52c41a' }}>¥{Number(v || 0).toFixed(2)}</b> },
                        { title: '账期 / 到期', dataIndex: 'month', width: 200, render: t => t || '-' },
                        { title: '当日', width: 60, render: (_, r) => r.is_today ? <Tag color="orange">今日</Tag> : null }
                      ]}
                      pagination={{
                        current: paidHistoryFilter.page,
                        pageSize: paidHistoryFilter.pageSize,
                        total: paidHistorySummary.total,
                        showSizeChanger: true,
                        showTotal: t => `共 ${t} 笔`,
                        onChange: (page, pageSize) => fetchPaidHistory({ page, pageSize })
                      }}
                      scroll={{ x: 900 }}
                      locale={{ emptyText: '暂无缴费记录' }}
                    />
                  </Card>
                </Col>
              </Row>
            ) : activeTab === 'butler' ? (
              <Card title="联系物业设置" style={{ maxWidth: 480 }}>
                <Form form={butlerForm} layout="vertical" onFinish={saveButler}>
                  <Form.Item name="butler_name" label="称呼（备用）" rules={[{ required: true, message: '请填写' }]}>
                    <Input placeholder="如：小德" />
                  </Form.Item>
                  <Form.Item name="butler_phone" label="呼叫电话" rules={[{ required: true, message: '请填写' }, { pattern: PHONE_OR_LANDLINE_REGEX, message: '请填写正确手机号或座机号' }]}>
                    <Input placeholder="如 15352130011 或 0935-8213001" maxLength={20} />
                  </Form.Item>
                  <Form.Item name="butler_work_am" label="工作时间上午">
                    <Input placeholder="如：08:00-12:00" />
                  </Form.Item>
                  <Form.Item name="butler_work_pm" label="工作时间下午">
                    <Input placeholder="如：14:00-18:00" />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={butlerSaveLoading}>保存</Button>
                  </Form.Item>
                </Form>
                <div style={{ fontSize: 12, color: '#999' }}>小程序首页「联系物业」展示上述电话与工作时间</div>
              </Card>
            ) : activeTab === 'houses' ? (
              <>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <Form form={houseSearchForm} layout="inline" onFinish={(v) => { setLastSearchParams(v); runHouseSearch(v); }}>
                      <Form.Item name="community_name" label="小区"><Input placeholder="支持模糊" style={{ width: 140 }} allowClear /></Form.Item>
                      <Form.Item name="building" label="楼栋"><Input placeholder="支持模糊" style={{ width: 100 }} allowClear /></Form.Item>
                      <Form.Item name="unit" label="单元"><Input placeholder="支持模糊" style={{ width: 90 }} allowClear /></Form.Item>
                      <Form.Item name="room" label="房号"><Input placeholder="支持模糊" style={{ width: 90 }} allowClear /></Form.Item>
                      <Form.Item name="usage_type" label="房屋用途"><Select allowClear placeholder="全部" style={{ width: 100 }} options={HOUSE_USAGE_OPTIONS} /></Form.Item>
                      <Form.Item name="has_garage" label="有无车库"><Select allowClear placeholder="全部" style={{ width: 100 }} options={HAS_GARAGE_FILTER_OPTIONS} /></Form.Item>
                      <Form.Item><Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={houseSearchLoading}>搜索</Button></Form.Item>
                    </Form>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { houseAddForm.resetFields(); houseAddForm.setFieldsValue({ usage_type: '住宅', has_garage: false, garage_area: 0 }); setHouseAddModalVisible(true); }}>添加房屋</Button>
                  </div>
                </Card>
                <Table columns={houseSearchColumns} dataSource={houseSearchResults} rowKey="id" loading={houseSearchLoading} pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }} locale={{ emptyText: '请设置条件后点击搜索，或点「添加房屋」先添加' }} />
              </>
            ) : activeTab === 'pointsManage' ? (
              <Card title="积分管理">
                <div style={{ padding: '8px 12px', marginBottom: 16, background: '#f0f7ff', border: '1px solid #91caff', borderRadius: 6, color: '#0958d9', fontSize: 13 }}>
                  积分获取规则：业主缴纳<b>物业费、停车费、各类社区费用</b>时，按倍率自动到账（默认 <b>1 元 = 1 积分</b>，可在「积分配置」调整）。同一笔账单只发放一次。
                </div>
                {pointsStats != null && (
                  <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}><Statistic title="总发放" value={pointsStats.total_earned} suffix="积分" /></Col>
                    <Col span={6}><Statistic title="总消耗" value={pointsStats.total_spent} suffix="积分" /></Col>
                    <Col span={6}><Statistic title="有积分户数" value={pointsStats.accounts} suffix="户" /></Col>
                    <Col span={6}><Statistic title="兑换订单数" value={pointsStats.redeem_orders} /></Col>
                  </Row>
                )}
                <Space style={{ marginBottom: 16 }} wrap>
                  <Button type="primary" onClick={() => Modal.confirm({ title: '补发历史缴费积分', content: '将扫描所有已支付账单，为之前未发放积分的业主一次性补发。本操作幂等，可多次执行。', okText: '执行补发', cancelText: '取消', onOk: () => fetch('/api/points/admin/backfill-pay', { method: 'POST', headers: getAuthHeaders() }).then(res => { const ok = res.ok; return res.json().then(d => { if (ok) { message.success(d.message || '补发完成'); fetchData(); } else message.error(d.error || '操作失败'); }); }) })}>补发历史缴费积分</Button>
                  <Button danger onClick={() => Modal.confirm({ title: '年度清零', content: '将把所有业主积分余额清零，不可恢复。确定执行？', okText: '确定清零', okType: 'danger', cancelText: '取消', onOk: () => fetch('/api/points/admin/clear', { method: 'POST', headers: getAuthHeaders() }).then(res => { const ok = res.ok; return res.json().then(d => { if (ok) { message.success(d.message || '清零完成'); fetchData(); } else message.error(d.error || '操作失败'); }); }) })}>立即年度清零</Button>
                  <Button onClick={async () => { try { const res = await fetch('/api/points/admin/export?type=log', { headers: getAuthHeaders() }); if (!res.ok) { message.error('下载失败'); return; } const blob = await res.blob(); const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = 'points-log.xlsx'; link.click(); URL.revokeObjectURL(link.href); message.success('已下载'); } catch (e) { message.error('下载失败'); } }}>导出流水</Button>
                  <Button onClick={async () => { try { const res = await fetch('/api/points/admin/export?type=order', { headers: getAuthHeaders() }); if (!res.ok) { message.error('下载失败'); return; } const blob = await res.blob(); const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = 'points-orders.xlsx'; link.click(); URL.revokeObjectURL(link.href); message.success('已下载'); } catch (e) { message.error('下载失败'); } }}>导出订单</Button>
                </Space>
                <Form form={pointsAdjustForm} layout="inline" onFinish={handlePointsAdjust} style={{ marginBottom: 24 }}>
                  <Form.Item name="phone" label="手机号" rules={[{ required: true }, { pattern: /^1\d{10}$/, message: '11位手机号' }]}><Input placeholder="业主手机号" style={{ width: 140 }} /></Form.Item>
                  <Form.Item name="amount" label="积分" rules={[{ required: true }]}><InputNumber placeholder="正数发放负数扣减" style={{ width: 140 }} /></Form.Item>
                  <Form.Item name="remark" label="备注"><Input placeholder="如：志愿活动奖励" style={{ width: 160 }} /></Form.Item>
                  <Form.Item><Button type="primary" htmlType="submit" loading={pointsAdjustSubmitting}>提交</Button></Form.Item>
                </Form>

                <Card type="inner" title="业主积分账户" extra={
                  <Space>
                    <Input.Search
                      placeholder="搜手机号 / 姓名 / 小区 / 楼栋 / 房号"
                      allowClear
                      onSearch={v => setPointsAccountKeyword(v || '')}
                      onChange={e => { if (!e.target.value) setPointsAccountKeyword(''); }}
                      style={{ width: 280 }}
                    />
                    <Button onClick={() => fetchData()}>刷新</Button>
                  </Space>
                }>
                  <Table
                    size="small"
                    rowKey="phone"
                    loading={pointsAccountLoading}
                    dataSource={pointsAccountKeyword
                      ? pointsAccountData.filter(r => {
                          const kw = pointsAccountKeyword.toLowerCase();
                          return (r.phone || '').toLowerCase().includes(kw)
                            || (r.owner_name || '').toLowerCase().includes(kw)
                            || (r.community || '').toLowerCase().includes(kw)
                            || (r.building || '').toLowerCase().includes(kw)
                            || (r.unit || '').toLowerCase().includes(kw)
                            || (r.room || '').toLowerCase().includes(kw);
                        })
                      : pointsAccountData}
                    columns={[
                      { title: '手机号', dataIndex: 'phone', width: 120 },
                      { title: '业主姓名', dataIndex: 'owner_name', width: 100, render: v => v || <span style={{ color: '#bbb' }}>未绑定</span> },
                      { title: '小区', dataIndex: 'community', width: 140, render: v => v || '-' },
                      { title: '楼栋', dataIndex: 'building', width: 80, render: v => v ? (String(v).match(/号楼|栋|号/) ? v : v + '号楼') : '-' },
                      { title: '单元', dataIndex: 'unit', width: 70, render: v => v ? (String(v).includes('单元') ? v : v + '单元') : '-' },
                      { title: '房号', dataIndex: 'room', width: 80, render: v => v ? (String(v).includes('室') ? v : v + '室') : '-' },
                      { title: '当前余额', dataIndex: 'balance', width: 100, sorter: (a, b) => (a.balance || 0) - (b.balance || 0), render: v => <b style={{ color: '#1677ff' }}>{v || 0}</b> },
                      { title: '累计获得', dataIndex: 'total_earned', width: 100, sorter: (a, b) => (a.total_earned || 0) - (b.total_earned || 0) },
                      { title: '累计消耗', dataIndex: 'total_spent', width: 100, sorter: (a, b) => (a.total_spent || 0) - (b.total_spent || 0) },
                      { title: '最后更新', dataIndex: 'updated_at', width: 165, render: t => t ? String(t).replace('T', ' ').slice(0, 19) : '-' },
                      { title: '操作', width: 230, fixed: 'right', render: (_, r) => (
                        <Space size={4} wrap>
                          <Button type="link" size="small" onClick={() => { pointsAdjustForm.setFieldsValue({ phone: r.phone, amount: undefined, remark: '' }); message.info('已填入手机号，请在上方填写积分'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>调整</Button>
                          <Button type="link" size="small" onClick={() => Modal.confirm({
                            title: '单独清零',
                            content: <div>
                              <p>将把以下业主的积分余额<b>清零（保留账户）</b>，并写一条流水记录，<b>不可恢复</b>。</p>
                              <p style={{ background: '#fffbe6', padding: 8, borderRadius: 4 }}>
                                手机号：<b>{r.phone}</b><br />
                                业主：{r.owner_name || '未绑定'}<br />
                                地址：{[r.community, r.building && (String(r.building).match(/号楼|栋|号/) ? r.building : r.building + '号楼'), r.unit && (String(r.unit).includes('单元') ? r.unit : r.unit + '单元'), r.room && (String(r.room).includes('室') ? r.room : r.room + '室')].filter(Boolean).join(' ') || '-'}<br />
                                当前余额：<b style={{ color: '#fa541c' }}>{r.balance} 积分</b>
                              </p>
                            </div>,
                            okText: '确定清零',
                            okType: 'danger',
                            cancelText: '取消',
                            onOk: () => fetch('/api/points/admin/account/clear', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ phone: r.phone }) })
                              .then(async res => { const d = await res.json().catch(() => ({})); if (res.ok) { message.success(d.message || '已清零'); fetchData(); } else message.error(d.error || '操作失败'); })
                          })}>清零</Button>
                          {currentUser?.role === 'SUPER_ADMIN' && (
                            <Button type="link" size="small" danger onClick={() => Modal.confirm({
                              title: '删除积分账户',
                              content: <div>
                                <p>将彻底删除该业主的<b>积分账户、积分流水、签到记录</b>，<b>不可恢复</b>。</p>
                                <p>历史兑换订单将作为记录保留。</p>
                                <p style={{ background: '#fff1f0', padding: 8, borderRadius: 4 }}>
                                  手机号：<b>{r.phone}</b><br />
                                  业主：{r.owner_name || '未绑定'}<br />
                                  当前余额：<b>{r.balance} 积分</b>
                                </p>
                              </div>,
                              okText: '确定删除',
                              okType: 'danger',
                              cancelText: '取消',
                              onOk: () => fetch('/api/points/admin/account/delete', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ phone: r.phone }) })
                                .then(async res => {
                                  const d = await res.json().catch(() => ({}));
                                  if (res.ok) { message.success(d.message || '已删除'); fetchData(); return; }
                                  if (res.status === 409 && d.pending) {
                                    Modal.confirm({
                                      title: '该业主仍有未完成的兑换订单',
                                      content: `还有 ${d.pending} 笔未发放的兑换订单。若强制删除，订单将作为历史记录保留（不发放）。确定继续？`,
                                      okText: '强制删除',
                                      okType: 'danger',
                                      cancelText: '取消',
                                      onOk: () => fetch('/api/points/admin/account/delete', { method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ phone: r.phone, force: true }) })
                                        .then(async r2 => { const d2 = await r2.json().catch(() => ({})); if (r2.ok) { message.success(d2.message || '已删除'); fetchData(); } else message.error(d2.error || '操作失败'); })
                                    });
                                  } else {
                                    message.error(d.error || '操作失败');
                                  }
                                })
                            })}>删除</Button>
                          )}
                        </Space>
                      ) }
                    ]}
                    pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `共 ${t} 户` }}
                    scroll={{ x: 1300 }}
                    locale={{ emptyText: '暂无业主积分账户' }}
                  />
                </Card>
              </Card>
            ) : activeTab === 'pointsConfig' ? (
              <Card title="积分配置">
                <Form form={pointsConfigForm} layout="vertical" onFinish={handlePointsConfigSave} style={{ maxWidth: 560 }}>
                  <Card type="inner" title="缴费送积分（主获取渠道）" style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 12, color: '#555', fontSize: 13 }}>
                      业主缴纳「物业费、停车费及小区各类费用」时，系统自动按倍率发放积分。<br />
                      默认倍率 <b>1.0</b>，即每缴 <b>1 元 = 1 积分</b>；同一笔账单只发放一次。
                    </div>
                    <Form.Item name="pay_ratio" label="缴费积分倍率（元 → 积分）" initialValue={1}>
                      <InputNumber min={0.1} step={0.1} style={{ width: 160 }} placeholder="默认 1" />
                    </Form.Item>
                  </Card>
                  <Card type="inner" title="积分清零规则" style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 12, color: '#555', fontSize: 13 }}>
                      启用后，系统会在「指定日期 00:00 之后」自动把所有业主积分余额清零（流水会记一条 CLEAR）。
                    </div>
                    <Form.Item name="clear_enabled" label="启用自动清零" valuePropName="checked">
                      <Switch checkedChildren="开" unCheckedChildren="关" />
                    </Form.Item>
                    <Form.Item name="clear_date" label="年度清零日期（格式 MM-DD，如 12-31）" rules={[{ pattern: /^\d{1,2}-\d{1,2}$/, message: '格式如 12-31' }]}>
                      <Input placeholder="12-31" style={{ width: 160 }} />
                    </Form.Item>
                  </Card>
                  <Card type="inner" title="兑换 / 其他" style={{ marginBottom: 16 }}>
                    <Form.Item name="redeem_limit_month" label="每人每月最多兑换次数（0 表示不限）"><InputNumber min={0} precision={0} style={{ width: 160 }} /></Form.Item>
                    <Form.Item name="checkin_daily" label="每日签到积分（不需要可设 0）"><InputNumber min={0} precision={0} style={{ width: 160 }} /></Form.Item>
                    <Form.Item name="streak7" label="连续签到 7 天额外积分（不需要可设 0）"><InputNumber min={0} precision={0} style={{ width: 160 }} /></Form.Item>
                    <Form.Item name="month25" label="当月满 25 天额外积分（不需要可设 0）"><InputNumber min={0} precision={0} style={{ width: 160 }} /></Form.Item>
                  </Card>
                  <Form.Item><Button type="primary" htmlType="submit" loading={pointsConfigSubmitting}>保存配置</Button></Form.Item>
                </Form>
              </Card>
            ) : (
              <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                  <Space>
                    {activeTab === 'audit' && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => { bindAddForm.resetFields(); setIsBindAddModalVisible(true); }}>
                          直接添加业主
                        </Button>
                      )}
                    {(activeTab === 'parking' || activeTab === 'parkingBills') && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => activeTab === 'parking' ? (parkingSpaceForm.resetFields(), setIsParkingSpaceModalVisible(true)) : openParkingBillModal()}>
                        {activeTab === 'parking' ? '添加车位' : '添加停车费'}
                      </Button>
                    )}
                    {activeTab === 'pointsPrize' && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingPointsPrizeId(null); pointsPrizeForm.resetFields(); setPointsPrizeFileList([]); setIsPointsPrizeModalVisible(true); }}>添加奖品</Button>
                    )}
                    {activeTab !== 'repairs' && activeTab !== 'audit' && activeTab !== 'feedback' && activeTab !== 'parking' && activeTab !== 'parkingBills' && activeTab !== 'pointsPrize' && activeTab !== 'pointsOrders' && (
                      <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                          {activeTab === 'bills' ? '录入账单' : activeTab === 'communities' ? '新增小区' : activeTab === 'menus' ? '新增菜单' : '发布公告'}
                        </Button>
                        {activeTab === 'bills' && (
                          <>
                            <Upload beforeUpload={handleImport} showUploadList={false} accept=".xlsx,.xls,.csv">
                              <Button icon={<CloudUploadOutlined />}>批量导入</Button>
                            </Upload>
                            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>下载模板</Button>
                          </>
                        )}
                      </Space>
                    )}
                    {activeTab === 'bills' && selectedRowKeys.length > 0 && (
                      <Button type="primary" icon={<NotificationOutlined />} loading={urgeLoading} onClick={batchUrgeSelected}>
                        批量催缴（{selectedRowKeys.length}）
                      </Button>
                    )}
                    {batchDeleteSupported && selectedRowKeys.length > 0 && (
                      <Button type="primary" danger icon={<DeleteOutlined />} onClick={batchDeleteSelected}>
                        批量删除（{selectedRowKeys.length}）
                      </Button>
                    )}
                  </Space>
                  <Input.Search placeholder={activeTab === 'bills' ? '搜索金额/月份/房屋...' : activeTab === 'notices' ? '搜索公告标题...' : '搜索...'} allowClear onChange={e => setSearchText(e.target.value)} style={{ width: 280 }} />
                </div>
                {activeTab === 'bills' && (
                  <Card size="small" style={{ marginBottom: 16 }} title="批量生成">
                    <Form form={generateForm} layout="inline" onFinish={handleGenerate} initialValues={{ bill_type: '物业费' }}>
                      <Form.Item name="community_id" label="小区">
                        <Select placeholder="不选=全部小区" allowClear style={{ width: 140 }} options={communityList.map(c => ({ label: c.name, value: c.id }))} />
                      </Form.Item>
                      <Form.Item label="收账类型">
                        <Select options={[{ label: '按日期段收（物业费等）', value: 'year' }, { label: '按月收（电费等）', value: 'month' }]} value={generateType} onChange={v => { setGenerateType(v); generateForm.resetFields(['generate_period_range', 'generate_period_month']); }} style={{ width: 180 }} />
                      </Form.Item>
                      <Form.Item name="bill_type" label="费用类型">
                        <Select options={BILL_TYPE_OPTIONS.filter(o => o.value !== '车库费' || !generateFormCommunityId || communityHasGarage(selectedGenerateCommunity))} style={{ width: 140 }} />
                      </Form.Item>
                      {generateBillType === '车库费' && (
                        <span style={{ color: '#fa8c16', fontSize: 12, alignSelf: 'center' }}>
                          {generateFormCommunityId
                            ? (communityHasGarage(selectedGenerateCommunity) ? '仅对有车库且已填车库面积的房屋生成' : '该小区未配套车库，请先在小区管理开启')
                            : '请选择小区；未配套车库的小区不会生成车库费'}
                        </span>
                      )}
                      {generateType === 'year' ? (
                        <Form.Item name="generate_period_range" label="收费周期" rules={[{ required: true, message: '请选择开始和结束日期' }]}>
                          <RangePicker format="YYYY-MM-DD" style={{ width: 260 }} />
                        </Form.Item>
                      ) : (
                        <Form.Item name="generate_period_month" label="收费周期" rules={[{ required: true, message: '请选择月份' }]}>
                          <DatePicker picker="month" format="YYYY-MM" style={{ width: 140 }} />
                        </Form.Item>
                      )}
                      <Form.Item name="amount" label={generateType === 'year' && generateMode === 'by_area' ? (generateBillType === '车库费' ? '车库单价(元/平米/年)' : '房屋单价(元/平米/年)') : '金额(元)'} rules={[{ required: true, message: '必填' }, { pattern: /^\d+(\.\d{1,2})?$/, message: '有效金额' }]}>
                        <Input placeholder={generateType === 'year' && generateMode === 'by_area' ? '2.5' : (generateType === 'year' ? '2400' : '200')} style={{ width: 120 }} />
                      </Form.Item>
                      {generateType === 'year' && generateMode === 'by_area' && generateBillType !== '车库费' && (
                        <Form.Item name="unit_price_basement" label="地下室单价(元/平米/年)" rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '有效金额' }]}>
                          <Input placeholder="如 1.2，不填则按0" style={{ width: 120 }} />
                        </Form.Item>
                      )}
                      {generateType === 'year' && generateBillType !== '车库费' && (
                        <Form.Item label="计费方式">
                          <Select options={[{ label: '固定金额', value: 'fixed' }, { label: '按面积×单价', value: 'by_area' }]} value={generateMode} onChange={setGenerateMode} style={{ width: 140 }} />
                        </Form.Item>
                      )}
                      <Form.Item>
                        <Button type="primary" htmlType="submit" loading={generateLoading} disabled={generateGarageBillDisabled}>一键生成</Button>
                        <Button style={{ marginLeft: 8 }} onClick={openAreaModal}>补填面积</Button>
                      </Form.Item>
                    </Form>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                      {generateType === 'year' 
                        ? (generateBillType === '车库费'
                          ? '车库费：仅对配套车库小区中「有车库」且已填车库面积的房屋生成；金额=车库面积×单价（或固定金额）。'
                          : (generateMode === 'by_area' ? '按面积×单价：每户物业费=房屋面积×房屋单价+地下室面积×地下室单价。请选择开始日期和结束日期。' : '物业费等请直接选择开始日期和结束日期；相同周期账单会自动跳过'))
                        : '按月：为该小区所有房屋生成当月账单（如电费），金额相同可批量；不同则用Excel导入'}
                    </div>
                  </Card>
                )}
                <Table
                  columns={activeTab === 'bills' ? billColumns : activeTab === 'repairs' ? repairColumns : activeTab === 'communities' ? communityColumns : activeTab === 'menus' ? menuColumns : activeTab === 'audit' ? auditColumns : activeTab === 'feedback' ? feedbackColumns : activeTab === 'parking' ? parkingColumns : activeTab === 'parkingBills' ? parkingBillColumns : activeTab === 'pointsPrize' ? pointsPrizeColumns : activeTab === 'pointsOrders' ? pointsOrderColumns : noticeColumns}
                  dataSource={filteredData}
                  loading={loading}
                  rowKey="id"
                  rowSelection={(batchDeleteSupported || activeTab === 'bills') ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
                  pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条`, pageSizeOptions: ['10', '20', '50'] }}
                  locale={{ emptyText: activeTab === 'bills' ? '暂无账单，可点击「录入账单」或「批量导入」' : activeTab === 'repairs' ? '暂无报修记录' : activeTab === 'notices' ? '暂无公告，点击「发布公告」添加' : activeTab === 'feedback' ? '暂无投诉建议' : activeTab === 'parking' ? '暂无车位，点击「添加车位」' : activeTab === 'parkingBills' ? '暂无停车费账单，点击「添加停车费」' : activeTab === 'pointsPrize' ? '暂无奖品，点击「添加奖品」' : activeTab === 'pointsOrders' ? '暂无兑换订单' : '暂无数据' }}
                />
              </>
            )}
          </Content>
        </Layout>
      </Layout>
      
      <Modal title="补填面积（仅填面积，无需重新录入房屋）" open={isAreaModalVisible} onCancel={() => setIsAreaModalVisible(false)} footer={null} width={680}>
        <p style={{ color: '#666', marginBottom: 12 }}>以下房屋未填房屋面积或地下室面积，填写后点「保存」。至少填一项。</p>
        <Table
          size="small"
          dataSource={noAreaHouseList}
          rowKey="id"
          pagination={false}
          columns={[
            { title: '楼栋', dataIndex: 'building', width: 70 },
            { title: '单元', dataIndex: 'unit', width: 70 },
            { title: '房号', dataIndex: 'room', width: 70 },
            {
              title: '房屋面积(平米)',
              dataIndex: 'id',
              width: 110,
              render: (id) => (
                <Input
                  type="number"
                  placeholder="如 120"
                  value={areaInputValues[id] ?? ''}
                  onChange={e => setAreaInputValues(prev => ({ ...prev, [id]: e.target.value }))}
                />
              )
            },
            {
              title: '地下室面积(平米)',
              dataIndex: 'id',
              width: 110,
              render: (id) => (
                <Input
                  type="number"
                  placeholder="如 10"
                  value={basementInputValues[id] ?? ''}
                  onChange={e => setBasementInputValues(prev => ({ ...prev, [id]: e.target.value }))}
                />
              )
            },
            {
              title: '操作',
              width: 80,
              render: (_, row) => (
                <Button
                  type="link"
                  size="small"
                  loading={areaSaveLoading}
                  onClick={() => saveHouseArea(row.id, areaInputValues[row.id], basementInputValues[row.id])}
                >
                  保存
                </Button>
              )
            }
          ]}
        />
      </Modal>

      <Modal title="修改密码" open={isPwdModalVisible} onCancel={() => setIsPwdModalVisible(false)} footer={null}>
        <Form form={pwdForm} onFinish={handleChangePassword} layout="vertical">
          <Form.Item name="oldPassword" label="旧密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="confirmPassword" label="确认新密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" block>确认修改</Button></Form.Item>
        </Form>
      </Modal>

      <Modal title="派单给维修负责人" open={assignModalVisible} onCancel={() => setAssignModalVisible(false)} onOk={submitAssign} okText="确认派单" cancelText="取消">
        <Form form={assignForm} layout="vertical">
          <Form.Item name="handler" label="维修负责人" rules={[{ required: true, message: '请填写负责人姓名' }, { max: 50, message: '名称过长' }]}>
            <Input placeholder="例如：张师傅 / 维修一组" allowClear />
          </Form.Item>
          <div style={{ color: '#888', fontSize: 12 }}>派单后该报修单状态将变为「处理中」，并记录开始处理时间。</div>
        </Form>
      </Modal>

      <Modal title={editingItem ? "编辑" : "新增"} open={isModalVisible} onCancel={() => setIsModalVisible(false)} footer={null}>
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          {activeTab === 'bills' && (
            <>
              <Form.Item label="录入方式">
                <Segmented options={[{ label: '单笔录入', value: 'single' }, { label: '批量录入', value: 'batch' }]} value={billEntryMode} onChange={v => setBillEntryMode(v)} block />
              </Form.Item>
              <Form.Item name="community_id" label="小区" rules={[{ required: true, message: '请选择小区' }]}>
                <Select
                  placeholder="选择小区"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={communityList.map(c => ({ label: c.name, value: c.id }))}
                  onChange={(id) => {
                    setHouseList([]);
                    form.setFieldsValue({ house_id: undefined, house_ids: undefined });
                    if (id) fetch('/api/house/list?community_id=' + id, { headers: getAuthHeaders() }).then(r => r.json()).then(list => setHouseList(Array.isArray(list) ? list : []));
                  }}
                />
              </Form.Item>
              {billEntryMode === 'single' ? (
                <Form.Item name="house_id" label="房屋" rules={[{ required: true, message: '请选择房屋' }]} extra={!houseList.length ? '若已选小区仍无房屋，请先用「批量导入」添加小区与房屋' : null}>
                  <Select
                    placeholder="先选小区后再选房屋"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={houseList.map(h => ({
                      label: `${formatHouseLabel(h.building, h.unit, h.room)} · ${formatUsageType(h.usage_type)} · 车库${formatHasGarage(h.has_garage)}`,
                      value: h.id
                    }))}
                    notFoundContent={houseList.length ? undefined : '该小区暂无房屋，请用「批量导入」添加'}
                  />
                </Form.Item>
              ) : (
                <Form.Item name="house_ids" label="房屋（可多选）" rules={[{ required: true, type: 'array', min: 1, message: '请至少选择一户' }]} extra={!houseList.length ? '若已选小区仍无房屋，请先用「批量导入」添加小区与房屋' : null}>
                  <Select
                    mode="multiple"
                    placeholder="先选小区后再选多户"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={houseList.map(h => ({
                      label: `${formatHouseLabel(h.building, h.unit, h.room)} · ${formatUsageType(h.usage_type)} · 车库${formatHasGarage(h.has_garage)}`,
                      value: h.id
                    }))}
                    notFoundContent={houseList.length ? undefined : '该小区暂无房屋，请用「批量导入」添加'}
                  />
                </Form.Item>
              )}
              <Form.Item label=" " colon={false} extra="选好小区后，也可在此直接添加一户。房屋面积/地下室面积/用途/车库可选">
                <Space wrap>
                  <Input placeholder="楼栋" value={addHouseBuilding} onChange={e => setAddHouseBuilding(e.target.value)} style={{ width: 90 }} />
                  <Input placeholder="单元" value={addHouseUnit} onChange={e => setAddHouseUnit(e.target.value)} style={{ width: 90 }} />
                  <Input placeholder="房号" value={addHouseRoom} onChange={e => setAddHouseRoom(e.target.value)} style={{ width: 90 }} />
                  <Select placeholder="用途" value={addHouseUsageType} onChange={setAddHouseUsageType} style={{ width: 90 }} options={HOUSE_USAGE_OPTIONS} />
                  <Input placeholder="房屋面积(㎡)" value={addHouseArea} onChange={e => setAddHouseArea(e.target.value)} style={{ width: 110 }} />
                  <Input placeholder="地下室面积(㎡)" value={addHouseBasementArea} onChange={e => setAddHouseBasementArea(e.target.value)} style={{ width: 110 }} />
                  {communityHasGarage(selectedBillEntryCommunity) && (
                    <>
                      <Switch checked={addHouseHasGarage} onChange={setAddHouseHasGarage} checkedChildren="有车库" unCheckedChildren="无车库" />
                      {addHouseHasGarage && <Input placeholder="车库面积(㎡)" value={addHouseGarageArea} onChange={e => setAddHouseGarageArea(e.target.value)} style={{ width: 110 }} />}
                    </>
                  )}
                  <Button type="default" onClick={addHouseInBillModal} loading={addHouseLoading}>添加房屋</Button>
                </Space>
              </Form.Item>
              <Form.Item name="bill_type" label="账单类型" initialValue="物业费">
                <Select options={BILL_TYPE_OPTIONS.filter(o => o.value !== '车库费' || communityHasGarage(selectedBillEntryCommunity))} />
              </Form.Item>
              <Form.Item label="周期类型">
                <Segmented
                  options={[{ label: '按日期段', value: 'range' }, { label: '按月', value: 'month' }, { label: '按年', value: 'year' }]}
                  value={billPeriodMode}
                  onChange={v => {
                    setBillPeriodMode(v);
                    form.resetFields(['bill_period_range', 'bill_period_month', 'bill_period_year']);
                  }}
                  block
                />
              </Form.Item>
              <Form.Item name="amount" label="金额（元）" rules={[{ required: true, message: '请输入金额' }, { pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额' }]}><Input type="number" placeholder="例如 200" step="0.01" /></Form.Item>
              {billPeriodMode === 'range' ? (
                <Form.Item name="bill_period_range" label="周期" rules={[{ required: true, message: '请选择开始和结束日期' }]}>
                  <RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                </Form.Item>
              ) : billPeriodMode === 'month' ? (
                <Form.Item name="bill_period_month" label="周期" rules={[{ required: true, message: '请选择月份' }]}>
                  <DatePicker picker="month" format="YYYY-MM" style={{ width: '100%' }} />
                </Form.Item>
              ) : (
                <Form.Item name="bill_period_year" label="周期" rules={[{ required: true, message: '请选择年份' }]}>
                  <DatePicker picker="year" format="YYYY" style={{ width: '100%' }} />
                </Form.Item>
              )}
              <Form.Item name="mark_paid" label="线下已缴" valuePropName="checked" extra="业主已线下缴费时勾选，录入后直接显示为已缴">
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </>
          )}
          {activeTab === 'notices' && (
            <>
              <Form.Item name="title" label="标题" required><Input /></Form.Item>
              <Form.Item name="content" label="内容" required><TextArea rows={4} /></Form.Item>
              <Form.Item name="is_top" label="是否置顶" valuePropName="checked"><Switch /></Form.Item>
              <Form.Item label="公告图片">
                <Upload
                  action="/api/upload"
                  listType="picture"
                  fileList={fileList}
                  onChange={handleUploadChange}
                  name="file"
                  maxCount={1}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />}>点击上传</Button>
                </Upload>
              </Form.Item>
            </>
          )}
          {activeTab === 'communities' && (
            <>
              <Form.Item name="name" label="小区名称" required><Input /></Form.Item>
              <Form.Item name="address" label="小区地址" required><Input /></Form.Item>
              <Form.Item name="has_garage_facility" label="配套车库" valuePropName="checked" extra="开启后，该小区可生成/导入车库费，并按房屋是否带车库区分计费">
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
              <Form.Item name="garage_unit_price" label="车库参考单价(元/㎡/年)" extra="按面积生成车库费时可作参考，也可在批量生成时手动填写">
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="如 5" />
              </Form.Item>
            </>
          )}
          {activeTab === 'menus' && (
            <>
              <Form.Item name="name" label="菜单名称" rules={[{ required: true, message: '请输入菜单名称' }]}><Input placeholder="如：生活缴费" /></Form.Item>
              <Form.Item name="icon" label="图标" rules={[{ required: true, message: '请选择图标' }]}>
                <Select
                  placeholder="选择图标"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={[
                    { label: '🅿️ 停车/停车缴费', value: '🅿️' },
                    { label: '🚗 车辆/车位', value: '🚗' },
                    { label: '💰 生活缴费', value: '💰' },
                    { label: '🔧 在线报修', value: '🔧' },
                    { label: '📢 社区公告', value: '📢' },
                    { label: '📋 我的账单', value: '📋' },
                    { label: '🏠 我的房屋', value: '🏠' },
                    { label: '👤 个人中心', value: '👤' },
                    { label: '🛠 便民服务', value: '🛠' },
                    { label: '📦 物业服务', value: '📦' },
                    { label: '🔔 消息通知', value: '🔔' },
                    { label: '📞 联系物业', value: '📞' },
                    { label: '⚙ 设置', value: '⚙' },
                    { label: '📄 其他', value: '📄' },
                    { label: '💳 缴费', value: '💳' },
                    { label: '⚡ 水电', value: '⚡' },
                    { label: '🔑 门禁/钥匙', value: '🔑' },
                    { label: '📝 登记/报事', value: '📝' },
                    { label: '🏢 楼栋', value: '🏢' },
                    { label: '🌐 社区', value: '🌐' },
                    { label: '📱 服务', value: '📱' },
                    { label: '⭐ 收藏/推荐', value: '⭐' },
                    { label: '📅 预约/日历', value: '📅' },
                    { label: '🔒 安全', value: '🔒' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="page_path" label="跳转路径" rules={[{ required: true, message: '请输入跳转路径' }]}><Input placeholder="如：/pages/bills/bills" /></Form.Item>
              <Form.Item name="sort_order" label="排序(越小越前)" rules={[{ required: true }]}><InputNumber style={{width: '100%'}} min={0} /></Form.Item>
              <Form.Item name="is_visible" label="是否显示" valuePropName="checked"><Switch /></Form.Item>
            </>
          )}
          <Form.Item><Button type="primary" htmlType="submit" block>提交</Button></Form.Item>
        </Form>
      </Modal>

      <Modal title="编辑房屋" open={houseEditModalVisible} onCancel={() => setHouseEditModalVisible(false)} onOk={handleHouseEditSave} okText="保存">
        <Form form={houseEditForm} layout="vertical" preserve={false}>
          <Form.Item name="id" hidden><Input /></Form.Item>
          <Form.Item name="community_id" hidden><Input /></Form.Item>
          <Form.Item name="community_has_garage_facility" hidden><Input /></Form.Item>
          <Form.Item name="community_name" label="小区"><Input disabled /></Form.Item>
          <Form.Item name="building" label="楼栋"><Input disabled /></Form.Item>
          <Form.Item name="unit" label="单元"><Input disabled /></Form.Item>
          <Form.Item name="room" label="房号"><Input disabled /></Form.Item>
          <Form.Item name="usage_type" label="房屋用途" initialValue="住宅">
            <Select options={HOUSE_USAGE_OPTIONS} />
          </Form.Item>
          <Form.Item name="area" label="房屋面积(㎡)"><Input type="number" step="0.01" placeholder="选填" /></Form.Item>
          <Form.Item name="basement_area" label="地下室面积(㎡)"><Input type="number" step="0.01" placeholder="选填" /></Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.community_has_garage_facility !== cur.community_has_garage_facility}>
            {({ getFieldValue }) => getFieldValue('community_has_garage_facility') ? (
              <>
                <Form.Item name="has_garage" label="有无车库" valuePropName="checked">
                  <Switch checkedChildren="有" unCheckedChildren="无" />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.has_garage !== cur.has_garage}>
                  {({ getFieldValue: gf }) => gf('has_garage') ? (
                    <Form.Item name="garage_area" label="车库面积(㎡)" rules={[{ required: true, message: '请填写车库面积' }]}>
                      <Input type="number" step="0.01" placeholder="如 12.5" />
                    </Form.Item>
                  ) : null}
                </Form.Item>
              </>
            ) : (
              <div style={{ color: '#999', fontSize: 12, marginBottom: 12 }}>该小区未配套车库，无需填写车库信息</div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="添加房屋" open={houseAddModalVisible} onCancel={() => setHouseAddModalVisible(false)} onOk={handleHouseAdd} okText="添加" confirmLoading={houseAddLoading}>
        <Form form={houseAddForm} layout="vertical" preserve={false} initialValues={{ usage_type: '住宅', has_garage: false, garage_area: 0 }}>
          <Form.Item name="community_id" label="小区" rules={[{ required: true, message: '请先选小区' }]}>
            <Select options={communityList.map(c => ({ label: c.name, value: c.id }))} placeholder="先到小区管理新增小区" />
          </Form.Item>
          <Form.Item name="building" label="楼栋" rules={[{ required: true, message: '必填' }]}><Input placeholder="如 1 或 1栋" /></Form.Item>
          <Form.Item name="unit" label="单元" rules={[{ required: true, message: '必填' }]}><Input placeholder="如 1 或 1单元" /></Form.Item>
          <Form.Item name="room" label="房号" rules={[{ required: true, message: '必填' }]}><Input placeholder="如 101" /></Form.Item>
          <Form.Item name="usage_type" label="房屋用途">
            <Select options={HOUSE_USAGE_OPTIONS} />
          </Form.Item>
          <Form.Item name="area" label="房屋面积(㎡)"><Input type="number" step="0.01" placeholder="选填，按面积计费时用" /></Form.Item>
          <Form.Item name="basement_area" label="地下室面积(㎡)"><Input type="number" step="0.01" placeholder="选填" /></Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.community_id !== cur.community_id}>
            {({ getFieldValue }) => {
              const cid = getFieldValue('community_id');
              const c = communityList.find(x => x.id === cid);
              return communityHasGarage(c) ? (
                <>
                  <Form.Item name="has_garage" label="有无车库" valuePropName="checked">
                    <Switch checkedChildren="有" unCheckedChildren="无" />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(prev, cur) => prev.has_garage !== cur.has_garage}>
                    {({ getFieldValue: gf }) => gf('has_garage') ? (
                      <Form.Item name="garage_area" label="车库面积(㎡)">
                        <Input type="number" step="0.01" placeholder="如 12.5" />
                      </Form.Item>
                    ) : null}
                  </Form.Item>
                </>
              ) : null;
            }}
          </Form.Item>
          <Form.Item name="owner_name" label="业主姓名" extra="选填。老人当面办理时填写，保存后该手机号可直接登录并看到此房屋。"><Input placeholder="选填" /></Form.Item>
          <Form.Item name="owner_phone" label="业主手机号" extra="与业主姓名同时填写时，为该房屋绑定业主。" rules={[{ pattern: /^1\d{10}$|^$/, message: '请填11位手机号或留空' }]}><Input placeholder="11位手机号，选填" maxLength={11} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="直接添加业主" open={isBindAddModalVisible} onCancel={() => setIsBindAddModalVisible(false)} footer={null}>
        <Form form={bindAddForm} onFinish={handleBindAdd} layout="vertical" initialValues={{ usage_type: '住宅', has_garage: false, garage_area: 0 }}>
          <Form.Item name="community" label="小区" rules={[{ required: true, message: '请选择小区' }]}>
            <Select placeholder="选择小区" options={communityList.map(c => ({ label: c.name, value: c.name }))} />
          </Form.Item>
          <Form.Item name="building" label="楼栋" rules={[{ required: true }]}><Input placeholder="如 1栋 或 1" /></Form.Item>
          <Form.Item name="unit" label="单元" rules={[{ required: true }]}><Input placeholder="如 1单元 或 1" /></Form.Item>
          <Form.Item name="house" label="房号" rules={[{ required: true }]}><Input placeholder="如 101" /></Form.Item>
          <Form.Item name="name" label="业主姓名" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="phone" label="手机号" rules={[{ required: true, pattern: /^1\d{10}$/, message: '11位手机号' }]}><Input placeholder="11位手机号" /></Form.Item>
          <Form.Item name="usage_type" label="房屋用途"><Select options={HOUSE_USAGE_OPTIONS} /></Form.Item>
          <Form.Item name="area" label="房屋面积(平米)" extra="可选"><Input type="number" placeholder="如 80" step="0.01" /></Form.Item>
          <Form.Item name="basement_area" label="地下室面积(平米)" extra="可选"><Input type="number" placeholder="如 10" step="0.01" /></Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.community !== cur.community}>
            {({ getFieldValue }) => {
              const cName = getFieldValue('community');
              const c = communityList.find(x => x.name === cName);
              return communityHasGarage(c) ? (
                <>
                  <Form.Item name="has_garage" label="有无车库" valuePropName="checked">
                    <Switch checkedChildren="有" unCheckedChildren="无" />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(prev, cur) => prev.has_garage !== cur.has_garage}>
                    {({ getFieldValue: gf }) => gf('has_garage') ? (
                      <Form.Item name="garage_area" label="车库面积(㎡)"><Input type="number" step="0.01" placeholder="如 12.5" /></Form.Item>
                    ) : null}
                  </Form.Item>
                </>
              ) : null;
            }}
          </Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" block>添加</Button></Form.Item>
        </Form>
      </Modal>

      <Modal title="编辑业主信息" open={isAuditEditVisible} onCancel={() => { setIsAuditEditVisible(false); setEditingAuditId(null); }} footer={null}>
        <Form form={auditEditForm} onFinish={handleAuditEditSubmit} layout="vertical">
          <Form.Item name="name" label="业主姓名" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="phone" label="手机号" rules={[{ required: true, pattern: /^1\d{10}$/, message: '11位手机号' }]}><Input placeholder="11位手机号" /></Form.Item>
          <Form.Item name="usage_type" label="房屋用途"><Select options={HOUSE_USAGE_OPTIONS} /></Form.Item>
          <Form.Item name="area" label="房屋面积(平米)"><Input type="number" placeholder="选填" step="0.01" /></Form.Item>
          <Form.Item name="basement_area" label="地下室面积(平米)"><Input type="number" placeholder="选填" step="0.01" /></Form.Item>
          <Form.Item name="has_garage" label="有无车库" valuePropName="checked"><Switch checkedChildren="有" unCheckedChildren="无" /></Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.has_garage !== cur.has_garage}>
            {({ getFieldValue }) => getFieldValue('has_garage') ? (
              <Form.Item name="garage_area" label="车库面积(㎡)"><Input type="number" step="0.01" placeholder="如 12.5" /></Form.Item>
            ) : null}
          </Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" block loading={auditEditSubmitting}>保存</Button></Form.Item>
        </Form>
      </Modal>

      <Modal title="添加车位" open={isParkingSpaceModalVisible} onCancel={() => setIsParkingSpaceModalVisible(false)} footer={null}>
        <Form form={parkingSpaceForm} onFinish={handleParkingSpaceAdd} layout="vertical">
          <Form.Item name="community_id" label="小区" rules={[{ required: true, message: '请选择小区' }]}>
            <Select placeholder="选择小区" options={communityList.map(c => ({ label: c.name, value: c.id }))} />
          </Form.Item>
          <Form.Item name="code" label="车位编号" rules={[{ required: true, message: '必填' }]}><Input placeholder="如 A-001" /></Form.Item>
          <Form.Item name="phone" label="绑定手机号"><Input placeholder="选填，业主手机号" /></Form.Item>
          <Form.Item name="status" label="状态" initialValue="AVAILABLE">
            <Select options={[{ label: '空闲', value: 'AVAILABLE' }, { label: '已售', value: 'OWNED' }, { label: '已租', value: 'RENTED' }]} />
          </Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" block>添加</Button></Form.Item>
        </Form>
      </Modal>

      <Modal title="添加停车费" open={isParkingBillModalVisible} onCancel={() => setIsParkingBillModalVisible(false)} footer={null}>
        <Form form={parkingBillForm} onFinish={handleParkingBillAdd} layout="vertical">
          <Form.Item name="community_id" label="小区" rules={[{ required: true, message: '请选择小区' }]}>
            <Select
              placeholder="选择小区"
              options={communityList.map(c => ({ label: c.name, value: c.id }))}
              onChange={(value) => {
                parkingBillForm.setFieldsValue({ parking_space_id: undefined });
                loadParkingSpacesForBill(value);
              }}
            />
          </Form.Item>
          <Form.Item name="parking_space_id" label="车位编号" rules={[{ required: true, message: '请选择车位' }]}>
            <Select
              placeholder="先选小区，再选车位编号"
              showSearch
              optionFilterProp="label"
              options={parkingBillSpaceList.map(item => ({
                label: item.phone ? `${item.code}（${item.phone}）` : item.code,
                value: item.id
              }))}
              notFoundContent={parkingBillForm.getFieldValue('community_id') ? '该小区暂无车位' : '请先选择小区'}
            />
          </Form.Item>
          <Form.Item name="amount" label="金额(元)" rules={[{ required: true, message: '必填' }]}><Input type="number" step="0.01" placeholder="如 200" /></Form.Item>
          <Form.Item name="bill_type" label="类型" initialValue="月租">
            <Select
              options={PARKING_BILL_TYPE_OPTIONS}
              onChange={(value) => applyParkingBillTypeDefaults(value, true)}
            />
          </Form.Item>
          {parkingBillType !== '永久拥有' ? (
            <>
              <Form.Item
                name="parking_start_date"
                label="开始日期"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!['月租', '年租'].includes(parkingBillType)) return Promise.resolve();
                      if (value && value.isValid && value.isValid()) return Promise.resolve();
                      return Promise.reject(new Error('请选择开始日期'));
                    }
                  }
                ]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="请选择开始日期" />
              </Form.Item>
              <Form.Item
                name="parking_end_date"
                label="结束日期"
                extra={parkingBillType === '月租' ? '建议区间不要超过1个月' : parkingBillType === '年租' ? '建议区间不要超过12个月' : undefined}
                dependencies={['parking_start_date', 'bill_type']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!['月租', '年租'].includes(parkingBillType)) return Promise.resolve();
                      const start = parkingBillForm.getFieldValue('parking_start_date');
                      if (!value || !value.isValid || !value.isValid()) return Promise.reject(new Error('请选择结束日期'));
                      if (!start || !start.isValid || !start.isValid()) return Promise.reject(new Error('请先选择开始日期'));
                      if (!value.isAfter(start, 'day')) return Promise.reject(new Error('结束日期必须晚于开始日期'));
                      const maxMonths = parkingBillType === '年租' ? 12 : 1;
                      if (value.isAfter(start.add(maxMonths, 'month'))) {
                        return Promise.reject(new Error(`账期不能超过${maxMonths}个月`));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="请选择结束日期" />
              </Form.Item>
            </>
          ) : null}
          <Form.Item><Button type="primary" htmlType="submit" block loading={parkingBillSubmitLoading}>添加</Button></Form.Item>
        </Form>
      </Modal>

      <Modal title={editingPointsPrizeId ? '编辑奖品' : '添加奖品'} open={isPointsPrizeModalVisible} onCancel={() => { setIsPointsPrizeModalVisible(false); setEditingPointsPrizeId(null); setPointsPrizeFileList([]); }} footer={null}>
        <Form form={pointsPrizeForm} onFinish={handlePointsPrizeSubmit} layout="vertical">
          <Form.Item name="name" label="奖品名称" rules={[{ required: true }]}><Input placeholder="如：免费上门调试门禁" /></Form.Item>
          <Form.Item name="category" label="分类" rules={[{ required: true }]} initialValue="service"><Select options={POINTS_CATEGORIES} /></Form.Item>
          <Form.Item name="points_required" label="所需积分" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="image_url" label="奖品图片" hidden><Input /></Form.Item>
          <Form.Item label="奖品图片">
            <Upload
              action="/api/upload"
              headers={getAuthHeaders()}
              listType="picture-card"
              fileList={pointsPrizeFileList}
              name="file"
              maxCount={1}
              accept="image/*"
              onChange={({ fileList: newFileList }) => {
                setPointsPrizeFileList(newFileList);
                const done = newFileList.find(f => f.status === 'done' && (f.response?.url || f.url));
                const url = done ? (done.response?.url || done.url) : null;
                pointsPrizeForm.setFieldValue('image_url', url || undefined);
              }}
            >
              {pointsPrizeFileList.length >= 1 ? null : <span>点击上传</span>}
            </Upload>
          </Form.Item>
          <Form.Item name="description" label="兑换说明"><Input.TextArea rows={2} placeholder="选填" /></Form.Item>
          <Form.Item name="stock" label="库存" initialValue={0}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="valid_until" label="有效期"><Input placeholder="选填，如 2026-12-31" /></Form.Item>
          <Form.Item name="sort_order" label="排序" initialValue={0}><InputNumber style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="is_online" label="上架" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" block loading={pointsPrizeSubmitting}>保存</Button></Form.Item>
        </Form>
      </Modal>

      <Modal title="审核兑换订单" open={isPointsOrderAuditVisible} onCancel={() => { setIsPointsOrderAuditVisible(false); setAuditingOrderId(null); }} okText="通过" cancelText="取消" onOk={() => handlePointsOrderAudit('APPROVED')} footer={[
        <Button key="reject" danger onClick={() => handlePointsOrderAudit('REJECTED')} disabled={!pointsAuditRejectReason.trim()}>驳回</Button>,
        <Button key="cancel" onClick={() => setIsPointsOrderAuditVisible(false)}>取消</Button>,
        <Button key="ok" type="primary" onClick={() => handlePointsOrderAudit('APPROVED')}>通过</Button>
      ]}>
        <Form.Item label="驳回原因（驳回时必填）"><Input.TextArea rows={2} value={pointsAuditRejectReason} onChange={e => setPointsAuditRejectReason(e.target.value)} placeholder="选填，驳回时必填" /></Form.Item>
      </Modal>
    </Layout>
  );
};

const App = () => (
  <ConfigProvider locale={zhCN}>
    <Router>
      <Routes>
        <Route path="/*" element={<MainLayout />} />
      </Routes>
    </Router>
  </ConfigProvider>
);

export default App;
